import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from './services/api';
import { wsClient, WebSocketChannel } from './services/websocket';
import { getSavedSessions, saveSession, deleteSession, exportSessions, importSessions } from './services/storageService';
import { getReplaySession, saveReplaySession, clearReplaySession, hasReplaySession } from './services/replayTradingService';
import { fetchCandleData, fetchTickData } from './services/marketService';
import { AnalysisResult, AnalysisMode, SignalType, CandleData, AppMode, Timeframe, SavedSession, ChatMessage, TickData } from './types';
import { stripBase64Prefix } from './utils/fileHelpers';
import { processReplayTick } from './utils/candleHelpers';
import debug from './utils/debug';
import UploadZone from './components/UploadZone';
import SignalGauge from './components/SignalGauge';
import StockTicker from './components/StockTicker';
import ChatInterface from './components/ChatInterface';
import FloatingGuruChat from './components/FloatingGuruChat';
import MarkdownRenderer from './components/MarkdownRenderer';
import LiveChart, { type LiveChartHandle } from './components/LiveChart';
import ChartSettingsPanel from './components/ChartSettingsPanel';
import ReplayControls from './components/ReplayControls';
import ReplayStartSelector from './components/ReplayStartSelector';
import VirtualTradingPanel from './components/VirtualTradingPanel';
import LoadingSpinner from './components/LoadingSpinner';
import ReplayTradingPanel from './components/ReplayTradingPanel';
import { LayoutSelector } from './components/LayoutSelector';
import { MultiChartGrid } from './components/MultiChartGrid';
import SignInMenu from './components/SignInMenu';
import { useChartLayout } from './hooks/useChartLayout';
import { useWebSocketUpdates } from './hooks/useWebSocketUpdates';
import { v4 as uuidv4 } from 'uuid'; // We might need uuid for trades, or just use Date.now()
import { useReplayAuthController } from './hooks/useReplayAuthController';
import { useReplaySubscriptionGate } from './hooks/useReplaySubscriptionGate';
import { useGuruSubscriptionGate } from './hooks/useGuruSubscriptionGate';
import { useApiAuth } from './hooks/useApiAuth';
import UpgradeModal from './components/UpgradeModal';
import ReplayUpgradePrompt from './components/ReplayUpgradePrompt';
import TermsOfServiceModal from './components/TermsOfServiceModal';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import InfluencerBanner from './components/InfluencerBanner';
import { useTermsOfServiceModal } from './hooks/useTermsOfServiceModal';
import { 
  updatePanelsForReplay, 
  truncatePanelDataForReplay, 
  isMultiLayout,
  getLowestTimeframe,
  TIMEFRAME_MS,
  type IndexedTick 
} from './services/multiLayoutReplayService';

// API Wrapper Functions for AI analysis (to maintain compatibility with original service signatures)

const analyzeChartImage = async (imageData: string, mimeType: string, prompt?: string, symbol?: string, timeframe?: string) => {
  const { data, error } = await api.analyzeImage(imageData, prompt, symbol, timeframe);
  if (error) {
    throw new Error(error);
  }
  return data as AnalysisResult;
};

const analyzeMarketData = async (
  symbol: string,
  candles: CandleData[],
  timeframe: string,
  previousAnalysis?: string,
  replayTime?: number
) => {
  const payload = {
    symbol,
    timeframe,
    candles,
    previousAnalysis,
    replayTime
  }
  const { data, error } = await api.analyzeData(payload, 'market');
  if (error) {
    throw new Error(error);
  }
  return data as AnalysisResult;
};

const analyzeMultiTimeframe = async (candleDataMap: Map<string, CandleData[]>, symbol: string) => {
  const dataObj = Object.fromEntries(candleDataMap);
  const { data, error } = await api.analyzeData({ timeframes: dataObj, symbol }, 'multi-timeframe');
  if (error) {
    throw new Error(error);
  }
  return data as AnalysisResult;
};

// WebSocket stream classes (wrapper for new WebSocket client)
class BinanceStream {
  constructor(private symbol: string, private timeframe: string, private dataSource: string, private onTick: (tick: TickData) => void) {
    debug.stream(`[BinanceStream] Subscribing to ${symbol}:${timeframe}`);
    wsClient.subscribe(WebSocketChannel.MARKET, symbol, timeframe);
    wsClient.on('update', (message) => {
      // Message structure: { type, channel, symbol, interval, data, timestamp }
      if (message.channel === 'candles' && message.symbol === symbol && message.interval === timeframe) {
        debug.stream(`[BinanceStream] Candle update received:`, message.data);
        onTick(message.data);
      }
    });
  }
  
  close() {
    wsClient.unsubscribe(WebSocketChannel.MARKET, this.symbol, this.timeframe);
  }
}

class StockStream {
  private currentCandle: CandleData | null = null;
  private updateHandler: ((message: any) => void) | null = null;
  private wildcardHandler: ((message: any) => void) | null = null;
  private lastTickVolume: number = 0; // Track last tick's cumulative volume

  constructor(
    private symbol: string,
    private timeframe: string,
    private onCandle: (candle: CandleData) => void,
    initialCandles?: CandleData[]
  ) {
    debug.stream(`[StockStream] Subscribing to ${symbol}:${timeframe}`);
    
    // Initialize with the last historical candle to continue from there
    if (initialCandles && initialCandles.length > 0) {
      const lastCandle = initialCandles[initialCandles.length - 1];
      // Use the last candle as the current candle so live ticks update it
      this.currentCandle = { ...lastCandle };
      // Initialize lastTickVolume to prevent volume spikes
      // Note: Schwab sends cumulative daily volume, so we don't have the exact value here
      // The first tick will calculate delta from 0, which may be inaccurate for the first update
      debug.stream(`[StockStream] Initialized with last candle:`, {
        time: new Date(lastCandle.time).toISOString(),
        close: lastCandle.close,
        volume: lastCandle.volume
      });
    }
    
    // Ensure WebSocket is connected before subscribing
    if (!wsClient.isConnected()) {
      console.warn('[StockStream] WebSocket not connected, waiting...');
      wsClient.connect().then(() => {
        console.log(`[StockStream] WebSocket connected, now subscribing to ${symbol}:${timeframe}`);
        // Subscribe to 'candles' channel - backend will route to appropriate stream
        // and broadcast back on 'ticks' channel for Schwab data
        wsClient.subscribe(WebSocketChannel.MARKET, symbol, timeframe);
      }).catch(err => {
        console.error('[StockStream] Failed to connect WebSocket:', err);
      });
    } else {
      console.log(`[StockStream] Subscribing to ${symbol}:${timeframe} (WS already connected)`);
      wsClient.subscribe(WebSocketChannel.MARKET, symbol, timeframe);
    }

    this.updateHandler = (message) => {
      debug.stream(`[StockStream] Handler called for type '${message.type}':`, {
        channel: message.channel,
        symbol: message.symbol,
        interval: message.interval,
        timeframe: message.timeframe,
        expectedSymbol: symbol,
        expectedInterval: timeframe,
        symbolMatch: message.symbol === symbol,
        intervalMatch: message.interval === timeframe,
        timeframeMatch: message.timeframe === timeframe,
        hasData: !!message.data
      });

      // Handle both tick data and candle data
      // Check both 'interval' and 'timeframe' fields since backend sends both
      const intervalMatch = message.interval === timeframe || message.timeframe === timeframe;
      if (message.symbol === symbol && intervalMatch) {
        // Check if this is tick data (has 'last' field) or candle data (has 'open' field)
        const data = message.data;
        if (data && (data.last !== undefined || data.tradeTime !== undefined)) {
          // Raw tick data from Schwab - aggregate into candles
          debug.stream(`[StockStream] Processing tick:`, data);
          this.processTick(data);
        } else if (data && data.open !== undefined) {
          // Pre-aggregated candle from backend
          debug.stream(`[StockStream] Candle update received:`, data);
          onCandle(data);
        } else {
          console.warn(`[StockStream] Unknown data format:`, data);
        }
      } else {
        debug.stream(`[StockStream] Message filtered out - symbol/interval mismatch`);
      }
    }

    // Listen for 'candle_update' message type from backend (Schwab sends tick data on this channel)
    wsClient.on('candle_update', this.updateHandler);
    
    // Debug: Also listen to all messages (store handler for cleanup)
    this.wildcardHandler = (msg) => {
      debug.stream('[StockStream] Wildcard caught message:', msg);
    };
    wsClient.on('*', this.wildcardHandler);
  }

  private processTick(tickData: any): void {
    const price = tickData.last || tickData.price;
    
    // Skip ticks with invalid price data
    if (!price || price === 0) {
      debug.stream('[StockStream] Skipping tick with invalid price:', tickData);
      return;
    }

    const tick: TickData = {
      time: new Date(tickData.time || tickData.tradeTime).getTime(),
      price: price,
      volume: tickData.volume || 0,
    };

    // Get timeframe in milliseconds
    const tfMs = this.getTimeframeMs(this.timeframe);
    const candleTime = Math.floor(tick.time / tfMs) * tfMs;

    if (!this.currentCandle || this.currentCandle.time !== candleTime) {
      // New candle period - emit previous if exists, start new one
      if (this.currentCandle) {
        debug.stream(`[StockStream] Emitting completed candle:`, this.currentCandle);
        this.onCandle(this.currentCandle);
      }

      // Calculate volume delta (Schwab sends cumulative daily volume)
      const volumeDelta = this.lastTickVolume > 0 ? Math.max(0, tick.volume - this.lastTickVolume) : 0;
      this.lastTickVolume = tick.volume;

      this.currentCandle = {
        time: candleTime,
        open: tick.price,
        high: tick.price,
        low: tick.price,
        close: tick.price,
        volume: volumeDelta,
        dateStr: new Date(candleTime).toISOString(),
      };
      
      // Emit new candle immediately
      this.onCandle({ ...this.currentCandle });
    } else {
      // Check if there are actual changes
      const prevHigh = this.currentCandle.high;
      const prevLow = this.currentCandle.low;
      const prevClose = this.currentCandle.close;
      const prevVolume = this.currentCandle.volume;

      // Calculate volume delta (Schwab sends cumulative daily volume)
      const volumeDelta = this.lastTickVolume > 0 ? Math.max(0, tick.volume - this.lastTickVolume) : 0;
      this.lastTickVolume = tick.volume;

      // Update current candle
      this.currentCandle.high = Math.max(this.currentCandle.high, tick.price);
      this.currentCandle.low = Math.min(this.currentCandle.low, tick.price);
      this.currentCandle.close = tick.price;
      this.currentCandle.volume += volumeDelta;

      // Only emit if something actually changed
      const hasChanges = 
        this.currentCandle.high !== prevHigh ||
        this.currentCandle.low !== prevLow ||
        this.currentCandle.close !== prevClose ||
        this.currentCandle.volume !== prevVolume;

      if (hasChanges) {
        this.onCandle({ ...this.currentCandle });
      }
    }
  }

  private getTimeframeMs(timeframe: string): number {
    const match = timeframe.match(/(\d+)([mhd])/);
    if (!match) return 5 * 60 * 1000; // default 5m

    const value = parseInt(match[1]);
    const unit = match[2];

    switch (unit) {
      case 'm':
        return value * 60 * 1000;
      case 'h':
        return value * 60 * 60 * 1000;
      case 'd':
        return value * 24 * 60 * 60 * 1000;
      default:
        return 5 * 60 * 1000;
    }
  }

  close() {
    if (this.updateHandler) {
      wsClient.off('candle_update', this.updateHandler);
      this.updateHandler = null;
    }
    if (this.wildcardHandler) {
      wsClient.off('*', this.wildcardHandler);
      this.wildcardHandler = null;
    }
    wsClient.unsubscribe(WebSocketChannel.MARKET, this.symbol, this.timeframe);
    this.currentCandle = null;
    this.lastTickVolume = 0;
  }
}

// Helper for localized timestamp
const formatTimestamp = (ts: number) => {
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const App: React.FC = () => {
  // Set up API authentication with Clerk tokens
  useApiAuth();

  // Mode State
  const [mode, setMode] = useState<AppMode>(AppMode.LIVE);
  
  // Upload Mode State
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageMimeType, setImageMimeType] = useState<string>('image/png');

  // Live Mode State
  const [liveTicker, setLiveTicker] = useState<string>('AAPL');
  const [tickerInput, setTickerInput] = useState<string>('AAPL'); // Local input state
  const [timeframe, setTimeframe] = useState<Timeframe>('5m');
  const [timezone, setTimezone] = useState<string>('America/New_York');
  const [candlesInternal, setCandlesInternal] = useState<CandleData[]>([]);
  const [dataSource, setDataSource] = useState<string>('unknown');
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(true); // Start true to prevent flash

  // Ref to the primary LiveChart instance for high-frequency tick updates
  const liveChartRef = useRef<LiveChartHandle | null>(null);

  // Track whether a replay session was restored on startup (so we don't override with a LIVE session)
  const didRestoreReplaySessionRef = useRef(false);

  // Helper to deduplicate candles by timestamp
  const deduplicateCandles = useCallback((candles: CandleData[]): CandleData[] => {
    const seen = new Set<number>();
    return candles.filter(candle => {
      if (seen.has(candle.time)) {
        return false;
      }
      seen.add(candle.time);
      return true;
    });
  }, []);

  // Optional: forward raw candle updates directly to LiveChart for high-frequency tick rendering
  const handleChartTickUpdate = useCallback((candle: CandleData) => {
    if (!liveChartRef.current) return;

    const tick: TickData = {
      symbol: liveTicker,
      price: candle.close,
      timestamp: Math.floor(candle.time / 1000),
      volume: candle.volume,
      session: 'regular',
    };

    liveChartRef.current.updateFromTick(tick);
  }, [liveTicker]);

  // Wrapper for setCandles that always deduplicates
  const setCandles = useCallback((candlesOrUpdater: CandleData[] | ((prev: CandleData[]) => CandleData[])) => {
    if (typeof candlesOrUpdater === 'function') {
      setCandlesInternal(prev => {
        const newCandles = candlesOrUpdater(prev);
        const deduped = deduplicateCandles(newCandles);
        if (newCandles.length !== deduped.length) {
          console.warn(`[App] Removed ${newCandles.length - deduped.length} duplicate candles`);
        }
        debug.stream(`[App] 🔄 setCandles (updater): ${prev.length} -> ${deduped.length} candles`);
        return deduped;
      });
    } else {
      const deduped = deduplicateCandles(candlesOrUpdater);
      if (candlesOrUpdater.length !== deduped.length) {
        console.warn(`[App] Removed ${candlesOrUpdater.length - deduped.length} duplicate candles`);
      }
      debug.stream(`[App] 🔄 setCandles (direct): ${deduped.length} candles, first: ${deduped[0] ? new Date(deduped[0].time).toLocaleString() : 'none'}, last: ${deduped[deduped.length-1] ? new Date(deduped[deduped.length-1].time).toLocaleString() : 'none'}`);
      setCandlesInternal(deduped);
    }
  }, [deduplicateCandles]);

  // Use deduplicated candles
  const candles = candlesInternal;

  // Shared Analysis State
  // We now store a history of analyses for the current session. Index 0 is the LATEST.
  const [analysisHistory, setAnalysisHistory] = useState<AnalysisResult[]>([]);
  const [analysisId, setAnalysisId] = useState<string>(''); 
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Derived current analysis
  const analysis = analysisHistory.length > 0 ? analysisHistory[0] : null;
  
  // Chat History State (for saving)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [latestChartScreenshot, setLatestChartScreenshot] = useState<string>('');

  // Persistence State (localStorage-only)
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // Terms of Service and Privacy Policy Modal State
  const { isTermsOpen, isPrivacyOpen, openTermsModal, closeTermsModal, openPrivacyModal, closePrivacyModal } = useTermsOfServiceModal();

  // Desktop Layout State
  const [activeTab, setActiveTab] = useState<'analysis' | 'chat' | 'replay'>('analysis');
  const [isBottomPanelExpanded, setIsBottomPanelExpanded] = useState(false);
  const [bottomPanelHeight, setBottomPanelHeight] = useState(300); // Default 300px
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [isAnalysisTextExpanded, setIsAnalysisTextExpanded] = useState(true);

  const [reAnalyzeCompletedAt, setReAnalyzeCompletedAt] = useState<number>(0);
  const analysisResultsRef = useRef<HTMLDivElement | null>(null);
  const [expandedHistoryItems, setExpandedHistoryItems] = useState<Set<number>>(new Set());
  const [isChartSettingsOpen, setIsChartSettingsOpen] = useState(false);

  useEffect(() => {
    if (!reAnalyzeCompletedAt) return;
    if (activeTab !== 'analysis') return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        analysisResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }, [reAnalyzeCompletedAt, activeTab]);

  // Multi-Chart Layout State
  const {
    layoutType,
    panels,
    syncCrosshair,
    syncTimeframe,
    syncSymbol,
    changeLayout,
    updatePanelSymbol,
    updatePanelTimeframe,
    updatePanel,
    updateAllPanels,
    toggleSync
  } = useChartLayout();

  // Store chart data for each panel
  const [panelDataMap, setPanelDataMap] = useState<Map<string, CandleData[]>>(new Map());
  
  // Per-panel history loading state
  const [panelHistoryState, setPanelHistoryState] = useState<Map<string, { isFetching: boolean; hasMore: boolean }>>(new Map());

  // --- REPLAY MODE STATE ---
  const [replayMode, setReplayMode] = useState<'off' | 'selecting' | 'active'>('off');
  const replayModeRef = useRef<'off' | 'selecting' | 'active'>('off'); // Ref for immediate access in callbacks
  const replayTimeRef = useRef<number>(0); // Ref for replay time - used in async callbacks to filter data
  const [replayType, setReplayType] = useState<'candle' | 'tick'>('candle'); // Default to candle replay
  const [replayState, setReplayState] = useState({
    isPlaying: false,
    speed: 1000, // ms
    currentIndex: 0,
    tickIndex: 0, // For intra-candle replay
    replayTime: 0, // Current market time in replay (ms timestamp)
    displayTime: 0 // Time to display in UI (ms timestamp)
  });
  // Store the FULL dataset here when we enter replay mode, so we can "reveal" it candle by candle
  const [fullHistory, setFullHistory] = useState<CandleData[]>([]); 
  const [tickBuffer, setTickBuffer] = useState<TickData[]>([]); // Ticks for current/next candle
  // Store full history for each panel in multi-layout replay (keyed by panel ID)
  const [panelFullHistory, setPanelFullHistory] = useState<Map<string, CandleData[]>>(new Map());
  // Backup panel data when entering 'selecting' mode (to restore if user cancels)
  const [panelDataBackup, setPanelDataBackup] = useState<Map<string, CandleData[]>>(new Map());

  // --- VIRTUAL TRADING STATE ---
  const [virtualTrades, setVirtualTrades] = useState<import('./types').VirtualTrade[]>([]);
  const [virtualBalance, setVirtualBalance] = useState(100000);
  const [isPaperTradingMinimized, setIsPaperTradingMinimized] = useState(false);
  
  // Calculate Equity
  const currentPrice = candles.length > 0 ? candles[candles.length - 1].close : 0;
  const openPnL = virtualTrades
    .filter(t => t.status === 'OPEN')
    .reduce((acc, t) => acc + (currentPrice - t.entryPrice) * t.quantity * (t.type === 'BUY' ? 1 : -1), 0);
  const virtualEquity = virtualBalance + openPnL;

  useEffect(() => {
    if (replayMode !== 'active') {
      setIsPaperTradingMinimized(false);
    }
  }, [replayMode]);

  // --- MULTI-CHART DATA FETCHING ---
  
  // Track which panels are currently being fetched to prevent duplicate requests
  const panelFetchingRef = useRef<Set<string>>(new Set());
  // Track if a fetch cycle is in progress to prevent React StrictMode double-invoke
  const isFetchCycleRunningRef = useRef(false);
  // Track panel data in a ref to avoid dependency issues
  const panelDataMapRef = useRef<Map<string, CandleData[]>>(panelDataMap);
  panelDataMapRef.current = panelDataMap;
  
  // Fetch data for all panels when panels change or when symbol/timeframe updates
  useEffect(() => {
    if (mode !== AppMode.LIVE || layoutType === '1x1') {
      console.log(`[App] Skipping panel data fetch: mode=${mode}, layoutType=${layoutType}`);
      return;
    }
    
    // Skip fetching during ACTIVE replay mode - active replay manages its own data
    // But allow fetching during 'selecting' mode so user can see candles to pick replay time
    if (replayModeRef.current === 'active') {
      console.log(`[App] Skipping panel data fetch during active replay mode`);
      return;
    }
    
    // Skip fetching when on home screen (no main chart loaded) UNLESS in selecting mode
    // In selecting mode, we need panel data for user to pick replay start time
    if (candles.length === 0 && replayModeRef.current !== 'selecting') {
      console.log(`[App] Skipping panel data fetch: on home screen (no candles loaded)`);
      return;
    }
    
    // Clean up panelFetchingRef for panels that now have data (successful fetches)
    for (const panelId of panelFetchingRef.current) {
      const hasData = panelDataMapRef.current.get(panelId);
      if (hasData && hasData.length > 0) {
        panelFetchingRef.current.delete(panelId);
      }
    }
    
    // Find panels that need data (don't have data yet and have a valid symbol)
    // Check BOTH the ref (for in-progress fetches) AND the state (for completed fetches)
    const panelsNeedingData = panels.filter(panel => {
      const existingData = panelDataMapRef.current.get(panel.id);
      const isFetching = panelFetchingRef.current.has(panel.id);
      const hasValidSymbol = panel.symbol && panel.symbol.trim().length > 0;
      const hasData = existingData && existingData.length > 0;
      
      if (isFetching) {
        console.log(`[App] Panel ${panel.id} is already being fetched, skipping`);
        return false;
      }
      if (hasData) {
        console.log(`[App] Panel ${panel.id} already has ${existingData.length} candles, skipping`);
        return false;
      }
      
      return hasValidSymbol;
    });
    
    if (panelsNeedingData.length === 0) {
      return;
    }
    
    // Guard against React StrictMode double-invoke or rapid re-renders
    if (isFetchCycleRunningRef.current) {
      console.log(`[App] Fetch cycle already running, skipping`);
      return;
    }
    
    console.log(`[App] Fetching data for ${panelsNeedingData.length} panels: ${panelsNeedingData.map(p => p.id).join(', ')}`);
    isFetchCycleRunningRef.current = true;
    
    const fetchPanelData = async (panelId: string, symbol: string, timeframe: string) => {
      if (panelFetchingRef.current.has(panelId)) {
        console.log(`[App] Panel ${panelId} already fetching, skipping`);
        return;
      }
      
      panelFetchingRef.current.add(panelId);
      try {
        console.log(`[App] Fetching panel ${panelId}: ${symbol} (${timeframe})`);
        const { data } = await fetchCandleData(symbol, timeframe as Timeframe);
        let dedupedData = deduplicateCandles(data || []);
        
        // During ACTIVE replay mode only, filter data to only include candles before replay time
        // Use replayTimeRef.current for async callbacks to avoid stale state
        // Only filter when 'active', not during 'selecting' (user hasn't picked a time yet)
        const replayTime = replayModeRef.current === 'active' && replayTimeRef.current > 0 ? replayTimeRef.current : null;
        if (replayTime && dedupedData.length > 0) {
          const originalLength = dedupedData.length;
          dedupedData = dedupedData.filter(c => c.time < replayTime);
          console.log(`[App] Panel ${panelId} filtered for replay: ${originalLength} -> ${dedupedData.length} candles (replay time: ${new Date(replayTime).toLocaleString()})`);
        }
        
        console.log(`[App] Fetched ${dedupedData.length} candles for panel ${panelId}`);
        setPanelDataMap(prev => {
          const newMap = new Map(prev);
          newMap.set(panelId, dedupedData);
          // Update ref immediately within setState to prevent race conditions
          panelDataMapRef.current = newMap;
          console.log(`[App] Panel data map now has ${newMap.size} entries`);
          return newMap;
        });
      } catch (error) {
        console.error(`[App] Failed to fetch data for panel ${panelId}:`, error);
        // Only clear on error - let the ref update handle successful fetches
        panelFetchingRef.current.delete(panelId);
      }
      // Don't clear panelFetchingRef here - it will be checked against panelDataMapRef
    };

    // Fetch data for panels that need it sequentially to avoid too many requests
    const fetchAllPanelsSequentially = async () => {
      try {
        for (const panel of panelsNeedingData) {
          await fetchPanelData(panel.id, panel.symbol, panel.timeframe);
          // Delay between requests to avoid rate limiting (Schwab has 100 req/15min limit)
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } finally {
        // Reset fetch cycle flag when done
        isFetchCycleRunningRef.current = false;
      }
    };
    fetchAllPanelsSequentially();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panels, mode, layoutType, deduplicateCandles, candles.length]); // Intentionally exclude panelDataMap - use ref instead to avoid re-fetch loops

  // Handle panel symbol change
  const handlePanelSymbolChange = useCallback(async (panelId: string, symbol: string) => {
    updatePanelSymbol(panelId, symbol);
    try {
      const panel = panels.find(p => p.id === panelId);
      if (panel) {
        const { data } = await fetchCandleData(symbol, panel.timeframe as Timeframe);
        const dedupedData = deduplicateCandles(data || []);
        setPanelDataMap(prev => {
          const newMap = new Map(prev);
          newMap.set(panelId, dedupedData);
          return newMap;
        });
      }
    } catch (error) {
      console.error(`Failed to fetch data for ${symbol}:`, error);
    }
  }, [panels, updatePanelSymbol]);

  // Handle panel timeframe change
  const handlePanelTimeframeChange = useCallback(async (panelId: string, timeframe: string) => {
    updatePanelTimeframe(panelId, timeframe);
    try {
      const panel = panels.find(p => p.id === panelId);
      if (panel) {
        const { data } = await fetchCandleData(panel.symbol, timeframe as Timeframe);
        const dedupedData = deduplicateCandles(data || []);
        setPanelDataMap(prev => {
          const newMap = new Map(prev);
          newMap.set(panelId, dedupedData);
          return newMap;
        });
        // Reset history state for this panel
        setPanelHistoryState(prev => {
          const newMap = new Map(prev);
          newMap.set(panelId, { isFetching: false, hasMore: true });
          return newMap;
        });
      }
    } catch (error) {
      console.error(`Failed to fetch data for timeframe ${timeframe}:`, error);
    }
  }, [panels, updatePanelTimeframe]);

  // Handle loading more history for a specific panel
  const handleLoadMorePanelHistory = useCallback(async (panelId: string, oldestTime: number) => {
    // Block history loading only during ACTIVE replay mode
    // 'selecting': ALLOW - user may scroll left to find replay start time
    // 'active': BLOCK - replay controls data via stepping, history loading would overwrite truncated data
    if (replayModeRef.current === 'active') {
      console.log(`[App] Blocking history load during active replay for panel ${panelId}`);
      return;
    }
    
    const historyState = panelHistoryState.get(panelId) || { isFetching: false, hasMore: true };
    
    console.log(`[App] handleLoadMorePanelHistory called: panelId=${panelId}, oldestTime=${oldestTime}, isFetching=${historyState.isFetching}, hasMore=${historyState.hasMore}`);
    
    if (historyState.isFetching || !historyState.hasMore) {
      console.log(`[App] Skipping history load: isFetching=${historyState.isFetching}, hasMore=${historyState.hasMore}`);
      return;
    }
    
    const panel = panels.find(p => p.id === panelId);
    if (!panel) return;
    
    // Update fetching state
    setPanelHistoryState(prev => {
      const newMap = new Map(prev);
      newMap.set(panelId, { ...historyState, isFetching: true });
      return newMap;
    });
    
    try {
      // If oldestTime is 0 or invalid, fetch initial data (no endTime = most recent candles)
      // Otherwise, fetch historical data before oldestTime for pagination
      const isInitialLoad = !oldestTime || oldestTime <= 0;
      const endTime = isInitialLoad ? undefined : oldestTime - 1;
      
      console.log(`[App] Loading ${isInitialLoad ? 'initial' : 'more'} history for panel ${panelId}: ${panel.symbol} ${panel.timeframe}`);
      const { data: historyData } = await fetchCandleData(panel.symbol, panel.timeframe as Timeframe, endTime);
      
      if (historyData && historyData.length > 0) {
        setPanelDataMap(prev => {
          const currentData = prev.get(panelId) || [];
          const candleMap = new Map<number, CandleData>();
          
          // During ACTIVE replay mode only, filter ALL candles to only include those before replay time
          // This ensures no candles after replay time are ever shown
          // Use replayTimeRef.current for async callbacks to avoid stale state
          // Only filter when 'active', not during 'selecting' (user hasn't picked a time yet)
          const replayTime = replayModeRef.current === 'active' && replayTimeRef.current > 0 ? replayTimeRef.current : null;
          const filteredHistoryData = replayTime 
            ? historyData.filter(c => c.time < replayTime)
            : historyData;
          
          // Also filter currentData during replay to enforce the replay time boundary
          const filteredCurrentData = replayTime
            ? currentData.filter(c => c.time < replayTime)
            : currentData;
          
          if (replayTime) {
            const historyFiltered = filteredHistoryData.length !== historyData.length;
            const currentFiltered = filteredCurrentData.length !== currentData.length;
            if (historyFiltered || currentFiltered) {
              console.log(`[App] Filtered for replay (time: ${new Date(replayTime).toLocaleString()}): history ${historyData.length} -> ${filteredHistoryData.length}, current ${currentData.length} -> ${filteredCurrentData.length}`);
            }
          }
          
          // Add history data first (filter out invalid candles with time <= 0)
          filteredHistoryData.forEach(c => {
            if (c.time > 0) candleMap.set(c.time, c);
          });
          // Then add current data (will overwrite any duplicates, filter out invalid)
          filteredCurrentData.forEach(c => {
            if (c.time > 0) candleMap.set(c.time, c);
          });
          
          const merged = Array.from(candleMap.values()).sort((a, b) => a.time - b.time);
          
          // Log if we filtered out invalid candles
          const invalidCount = (filteredHistoryData.length + filteredCurrentData.length) - merged.length;
          if (invalidCount > 0) {
            console.warn(`[App] Filtered out ${invalidCount} invalid candles (time <= 0) from panel ${panelId}`);
          }
          
          const newMap = new Map(prev);
          newMap.set(panelId, merged);
          
          console.log(`[App] Panel ${panelId} history loaded: ${currentData.length} -> ${merged.length} candles (fetched ${historyData.length})`);
          
          // Check if there's more history based on whether API returned full batch
          // If we got less than 100 candles, we've likely reached the end of available history
          const hasMore = historyData.length >= 100;
          setPanelHistoryState(prevState => {
            const newStateMap = new Map(prevState);
            newStateMap.set(panelId, { isFetching: false, hasMore });
            return newStateMap;
          });
          
          return newMap;
        });
      } else {
        // No more history available
        setPanelHistoryState(prev => {
          const newMap = new Map(prev);
          newMap.set(panelId, { isFetching: false, hasMore: false });
          return newMap;
        });
      }
    } catch (error) {
      console.warn(`[App] Failed to load history for panel ${panelId}:`, error);
      setPanelHistoryState(prev => {
        const newMap = new Map(prev);
        newMap.set(panelId, { ...historyState, isFetching: false });
        return newMap;
      });
    }
  }, [panels, panelHistoryState, deduplicateCandles]);

  // --- REPLAY LOGIC ---

  const [isFetchingTicks, setIsFetchingTicks] = useState(false);

  async function handleFetchLiveData(
    e?: React.FormEvent,
    tickerOverride?: string,
    timeframeOverride?: Timeframe,
    forceNew: boolean = false
  ) {
    return handleFetchLiveDataImpl(e, tickerOverride, timeframeOverride, forceNew);
  }

  const {
    ensureSignedInForReplay,
    isAuthLoaded,
    isSignedIn,
    isPendingReplayRedirect,
    enterReplaySelectingMode,
    cancelReplaySelectingMode,
  } = useReplayAuthController({
    replayMode,
    setReplayMode,
    replayModeRef,
    liveTicker,
    timeframe,
    setLiveTicker,
    setTickerInput,
    setTimeframe,
    handleFetchLiveData,
    candlesLength: candles.length,
    isFetchingData,
    isInitialLoading,
    isRestoringSession,
  });

  // Subscription gate for replay mode
  const {
    plan: subscriptionPlan,
    checkReplayAccess,
    showUpgradePrompt,
    upgradeFeature,
    closeUpgradePrompt,
    openFullUpgradeModal,
    isUpgradeModalOpen,
    closeUpgradeModal,
  } = useReplaySubscriptionGate();

  // Subscription gate for AI Guru analysis
  const {
    canAccessAiAnalysis,
    checkGuruAccess,
    showUpgradePrompt: showGuruUpgradePrompt,
    closeUpgradePrompt: closeGuruUpgradePrompt,
    isAutoSubscribing,
  } = useGuruSubscriptionGate();

  // Memoized callbacks for FloatingGuruChat to prevent re-renders on tick updates
  const handleGuruAnalyze = useCallback(() => {
    if (!checkGuruAccess()) return;
    handleAnalyzeCurrentChart();
  }, [checkGuruAccess]);

  const handleGuruReAnalyze = useCallback(() => {
    if (!checkGuruAccess()) return;
    handleReAnalyze();
  }, [checkGuruAccess]);

  // --- REPLAY LOGIC ---

  // 1. Enter "Selecting" Mode (User clicks chart to pick start)
  const handleReplayModeClick = async () => {
    if (replayMode === 'off') {
        // First check auth, then check subscription
        if (!ensureSignedInForReplay({ ticker: liveTicker, timeframe })) return;
        // Check subscription access for replay (candle mode by default)
        if (!checkReplayAccess(replayType, { ticker: liveTicker, timeframe })) return;
        // Backup panel data before entering selecting mode (to restore if user cancels)
        if (isMultiLayout(layoutType)) {
            console.log('[App] Backing up panel data before entering selecting mode');
            setPanelDataBackup(new Map(panelDataMap));
        }
        enterReplaySelectingMode();
    } else if (replayMode === 'selecting') {
        // User is cancelling from 'selecting' mode (didn't pick a date)
        // Restore panel data from backup
        console.log('[App] Cancelling replay selection, restoring panel data');
        setReplayMode('off');
        replayModeRef.current = 'off';
        if (isMultiLayout(layoutType) && panelDataBackup.size > 0) {
            setPanelDataMap(new Map(panelDataBackup));
            setPanelDataBackup(new Map());
        }
        return;
    } else {
        // Exit replay - close all open positions first
        const openTrades = virtualTrades.filter(t => t.status === 'OPEN');
        if (openTrades.length > 0 && currentPrice) {
            console.log(`[App] Auto-closing ${openTrades.length} open positions at exit`);
            const closedTrades = virtualTrades.map(t => {
                if (t.status === 'OPEN') {
                    const pnl = (currentPrice - t.entryPrice) * t.quantity * (t.type === 'BUY' ? 1 : -1);
                    return {
                        ...t,
                        status: 'CLOSED' as const,
                        exitPrice: currentPrice,
                        exitTime: getTradeTimestamp(),
                        pnl
                    };
                }
                return t;
            });
            
            // Calculate total PnL from closed positions
            const totalPnL = openTrades.reduce((acc, t) => {
                const pnl = (currentPrice - t.entryPrice) * t.quantity * (t.type === 'BUY' ? 1 : -1);
                return acc + pnl;
            }, 0);
            
            setVirtualTrades(closedTrades);
            setVirtualBalance(prev => prev + totalPnL);
            
            // Give state a moment to update before clearing
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        setReplayMode('off');
        replayModeRef.current = 'off';
        replayTimeRef.current = 0; // Reset replay time ref
        setReplayState(prev => ({ ...prev, isPlaying: false, currentIndex: 0, tickIndex: 0, replayTime: 0, displayTime: 0 }));
        if (fullHistory.length > 0) {
            setCandles(fullHistory); // Restore full data
            setFullHistory([]);
        }
        
        // Clear replay trading session from local storage
        await clearReplaySession();
        
        // Reset trades and balance
        setVirtualTrades([]);
        setVirtualBalance(100000);
        
        // Fetch latest data to fill any gap from replay duration
        if (mode === AppMode.LIVE && liveTicker) {
             setIsFetchingData(true);
 
             if (isMultiLayout(layoutType) && panels.length > 0 && panelFullHistory.size > 0) {
                 setPanelDataMap(() => {
                     const restored = new Map<string, CandleData[]>();
                     for (const panel of panels) {
                         const history = panelFullHistory.get(panel.id);
                         if (history && history.length > 0) {
                             restored.set(panel.id, history);
                         }
                     }
                     return restored;
                 });
             }
 
             Promise.all([
                 fetchCandleData(liveTicker, timeframe)
                     .then(({ data, source }) => {
                         setCandles(data);
                         setDataSource(source);
                     }),
                 isMultiLayout(layoutType) && panels.length > 0
                     ? Promise.all(
                           panels.map(async (panel) => {
                               try {
                                   const { data } = await fetchCandleData(panel.symbol, panel.timeframe as Timeframe);
                                   return { panelId: panel.id, data };
                               } catch {
                                   return null;
                               }
                           })
                       ).then((results) => {
                           setPanelDataMap((prev) => {
                               const next = new Map(prev);
                               for (const result of results) {
                                   if (!result) continue;
                                   if (result.data && result.data.length > 0) {
                                       next.set(result.panelId, result.data);
                                   }
                               }
                               return next;
                           });
                       })
                     : Promise.resolve(),
             ])
                 .catch((e) => console.error('Failed to refresh data after replay', e))
                 .finally(() => setIsFetchingData(false));
        }
    }
  };

  // 2. Start Replay at specific timestamp (Called from LiveChart click)
  const handleReplayStart = async (timestamp: number) => {
      console.log('[App] Replay Start Requested at:', timestamp, new Date(timestamp).toLocaleString('en-US', { timeZone: timezone }));
      if (!ensureSignedInForReplay({ ticker: liveTicker, timeframe })) return;
      // Check subscription access for the selected replay type
      if (!checkReplayAccess(replayType, { ticker: liveTicker, timeframe })) return;
      if (replayMode !== 'selecting') {
          console.warn('[App] Replay start ignored: wrong mode', replayMode);
          return;
      }
      
      // In multi-layout mode, use panel data for validation since panels may have more history
      // than the main candles array
      let dataSourceCandles = candles;
      if (isMultiLayout(layoutType) && panelDataMap.size > 0) {
          // Find the panel with the most historical data (earliest first candle)
          let earliestTime = Infinity;
          for (const [panelId, panelCandles] of panelDataMap) {
              if (panelCandles.length > 0 && panelCandles[0].time < earliestTime) {
                  earliestTime = panelCandles[0].time;
                  dataSourceCandles = panelCandles;
                  console.log(`[App] Using panel ${panelId} data for replay (has earliest data)`);
              }
          }
      }
      
      // Validate candles exist
      if (dataSourceCandles.length === 0) {
          console.error('[App] No candles available for replay');
          setError('No chart data available for replay. Please load a chart first.');
          return;
      }
      
      // Check if selected time is within available data range
      const firstCandleTime = dataSourceCandles[0].time;
      const lastCandleTime = dataSourceCandles[dataSourceCandles.length - 1].time;
      console.log('[App] Available candle range:', {
          first: new Date(firstCandleTime).toLocaleString('en-US', { timeZone: timezone }),
          last: new Date(lastCandleTime).toLocaleString('en-US', { timeZone: timezone }),
          requested: new Date(timestamp).toLocaleString('en-US', { timeZone: timezone }),
          source: isMultiLayout(layoutType) ? 'panelDataMap' : 'candles'
      });
      
      // If selected time is before first available candle, show error
      if (timestamp < firstCandleTime) {
          console.error('[App] Selected time is before available data');
          setError(`Selected time is before available chart data. Earliest available: ${new Date(firstCandleTime).toLocaleString('en-US', { timeZone: timezone })}`);
          return;
      }
      
      // Find index of this timestamp in the data source candles
      const index = dataSourceCandles.findIndex(c => c.time >= timestamp);
      console.log('[App] Found candle index:', index, 'of', dataSourceCandles.length);
      
      if (index !== -1) {
          // Get the selected candle time from the data source
          const selectedCandleTime = dataSourceCandles[index].time;
          
          // Debug: Check for gaps in history
          const nextFew = dataSourceCandles.slice(index, index + 5).map(c => ({
              i: dataSourceCandles.indexOf(c),
              time: c.time,
              date: new Date(c.time).toLocaleString()
          }));
          console.log('[App] Replay History Context:', nextFew);

          // For main candles, truncate to show only candles that have COMPLETED before replay time
          // A candle completes at: start time + timeframe duration
          // Excludes in-progress candles and future candles
          let truncatedCandles: CandleData[];
          
          if (isMultiLayout(layoutType) && dataSourceCandles !== candles) {
              // In multi-layout mode: use panel data source for main chart
              const { ms: lowestTfMs } = getLowestTimeframe(panels);
              truncatedCandles = dataSourceCandles.filter(c => {
                  const completionTime = c.time + lowestTfMs;
                  return completionTime <= selectedCandleTime;
              });
              console.log('[App] Using panel data for main chart truncation:', truncatedCandles.length, 'candles (completed before', new Date(selectedCandleTime).toLocaleString(), ')');
          } else {
              // Single layout or main candles available
              const mainTfMs = TIMEFRAME_MS[timeframe as keyof typeof TIMEFRAME_MS] || 300000;
              truncatedCandles = candles.filter(c => {
                  const completionTime = c.time + mainTfMs;
                  return completionTime <= selectedCandleTime;
              });
          }
          
          console.log('[App] 🔍 Replay Start Debug:', {
              dataSourceCandlesCount: dataSourceCandles.length,
              selectedIndex: index,
              truncatedCount: truncatedCandles.length,
              selectedCandleTime: new Date(selectedCandleTime).toLocaleString(),
              firstDataCandle: new Date(dataSourceCandles[0].time).toLocaleString(),
              lastDataCandle: new Date(dataSourceCandles[dataSourceCandles.length - 1].time).toLocaleString()
          });

          // Backup full data for stepping forward
          // In multi-layout mode, use dataSourceCandles if it has the replay period data
          // (main candles might only have recent data, but panel data has historical data)
          const historySource = isMultiLayout(layoutType) && dataSourceCandles !== candles 
              ? dataSourceCandles 
              : candles;
          setFullHistory([...historySource]);
          console.log('[App] 📊 fullHistory set from', isMultiLayout(layoutType) && dataSourceCandles !== candles ? 'panel data' : 'main candles', 'with', historySource.length, 'candles');
          
          // CRITICAL: Set replay mode FIRST to close WebSocket before truncating candles
          // This prevents live updates from arriving while we're setting up replay
          setReplayMode('active');
          replayModeRef.current = 'active';
          
          // Initialize replayTime and displayTime to the start of the selected candle
          // Use the selected candle time from the data source (which has the correct data)
          replayTimeRef.current = selectedCandleTime; // Update ref FIRST for async callbacks
          setReplayState(prev => ({ 
              ...prev, 
              currentIndex: index, 
              isPlaying: false, 
              tickIndex: 0, // Will be updated after tick buffer is loaded
              replayTime: selectedCandleTime,
              displayTime: selectedCandleTime // Initialize display time to selected candle time
          }));
          
          // Truncate candles AFTER setting replay mode to avoid race condition
          console.log('[App] 📊 Setting truncated candles:', truncatedCandles.length);
          
          // For tick replay, reset the volume of the last candle to 0
          // This prevents double-counting since we'll rebuild volume from ticks
          if (replayType === 'tick' && truncatedCandles.length > 0) {
              const lastCandle = truncatedCandles[truncatedCandles.length - 1];
              console.log('[App] 🔄 Resetting last candle volume for tick replay:', {
                  originalVolume: lastCandle.volume.toLocaleString(),
                  time: new Date(lastCandle.time).toLocaleString()
              });
              truncatedCandles[truncatedCandles.length - 1] = {
                  ...lastCandle,
                  volume: 0
              };
          }
          
          setCandles(truncatedCandles);
          
          // Initialize panel data for multi-layout replay
          // Fetch candle data for each panel's individual timeframe
          if (isMultiLayout(layoutType)) {
              const replayStartTime = selectedCandleTime;
              console.log('[App] 📊 Initializing multi-layout panel data for replay at:', new Date(replayStartTime).toLocaleString());
              
              // Sync all panel symbols to the main ticker for replay
              // In replay mode, all panels must use the same symbol
              panels.forEach(panel => {
                  if (panel.symbol !== liveTicker) {
                      console.log(`[App] 📊 Syncing panel ${panel.id} symbol from ${panel.symbol} to ${liveTicker} for replay`);
                      updatePanelSymbol(panel.id, liveTicker);
                  }
              });
              
              // For each panel, ensure we have data that covers the replay time
              // If a panel's data doesn't extend to the replay time, fetch fresh data
              const newPanelFullHistory = new Map<string, CandleData[]>();
              const newPanelDisplayData = new Map<string, CandleData[]>();
              
              for (const panel of panels) {
                  let panelData = panelDataMap.get(panel.id) || [];
                  
                  // Check if panel data covers the replay start time
                  const lastCandleTime = panelData.length > 0 ? panelData[panelData.length - 1].time : 0;
                  const coversReplayTime = lastCandleTime >= replayStartTime;
                  
                  if (!coversReplayTime) {
                      // Panel doesn't have data up to replay time - fetch fresh data
                      console.log(`[App] 📊 Panel ${panel.id} (${panel.timeframe}) data ends at ${new Date(lastCandleTime).toLocaleString()}, need data up to ${new Date(replayStartTime).toLocaleString()} - fetching...`);
                      try {
                          const { data: freshData } = await fetchCandleData(liveTicker, panel.timeframe as Timeframe);
                          if (freshData && freshData.length > 0) {
                              // Merge fresh data with existing (dedupe by time)
                              const mergedMap = new Map<number, CandleData>();
                              panelData.forEach(c => mergedMap.set(c.time, c));
                              freshData.forEach(c => mergedMap.set(c.time, c));
                              panelData = Array.from(mergedMap.values()).sort((a, b) => a.time - b.time);
                              console.log(`[App] 📊 Panel ${panel.id} merged data: ${panelData.length} candles, new range: ${new Date(panelData[0].time).toLocaleString()} to ${new Date(panelData[panelData.length-1].time).toLocaleString()}`);
                          }
                      } catch (error) {
                          console.warn(`[App] Failed to fetch data for panel ${panel.id}:`, error);
                      }
                  }
                  
                  if (panelData.length > 0) {
                      // Store FULL history for this panel (includes candles after replay start for stepping)
                      newPanelFullHistory.set(panel.id, [...panelData]);
                      
                      // Truncate for DISPLAY - show only candles that have COMPLETED before replay time
                      // A candle completes at: start time + timeframe duration
                      // Excludes in-progress candles and future candles
                      const panelTfMs = TIMEFRAME_MS[panel.timeframe as keyof typeof TIMEFRAME_MS] || 300000;
                      const truncatedData = panelData.filter(c => {
                          const completionTime = c.time + panelTfMs;
                          return completionTime <= replayStartTime;
                      });
                      
                      // Debug: Show last few candles and replay time to verify in-progress candle is included
                      const lastFewCandles = truncatedData.slice(-3).map(c => ({
                          time: new Date(c.time).toLocaleString(),
                          completesAt: new Date(c.time + panelTfMs).toLocaleString()
                      }));
                      console.log(`[App] 📊 Panel ${panel.id} (${panel.timeframe}): ${truncatedData.length} candles for display`);
                      console.log(`[App] 📊 Panel ${panel.id} last few candles:`, lastFewCandles);
                      console.log(`[App] 📊 Panel ${panel.id} replayStartTime: ${new Date(replayStartTime).toLocaleString()}`);
                      
                      newPanelDisplayData.set(panel.id, truncatedData);
                  } else {
                      console.warn(`[App] Panel ${panel.id} has no data for replay`);
                  }
              }
              
              // Set all panel data in one call to avoid race conditions
              setPanelDataMap(newPanelDisplayData);
              console.log('[App] 📊 Panel display data set for', newPanelDisplayData.size, 'panels');
              
              // Set the panel full history for replay stepping
              setPanelFullHistory(newPanelFullHistory);
              console.log('[App] 📊 Panel full history set for', newPanelFullHistory.size, 'panels');
          }
          
          // Try to restore previous replay session from local storage
          const savedSession = await getReplaySession();
          if (savedSession && savedSession.ticker === liveTicker && savedSession.timeframe === timeframe) {
              console.log('[App] Restoring replay session:', savedSession);
              setVirtualTrades(savedSession.trades);
              setVirtualBalance(savedSession.balance);
          } else {
              // Start fresh
              setVirtualTrades([]);
              setVirtualBalance(100000);
          }
          
          // Auto-switch to Replay Tab
          setActiveTab('replay');
          setIsBottomPanelExpanded(true);

          // Only fetch ticks if in tick replay mode
          if (replayType === 'tick') {
              // ROLLING 1-DAY WINDOW: Fetch only 1 day of tick data initially
              // Frontend will fetch next day when current buffer is consumed
              const dayMs = 24 * 60 * 60 * 1000;
              const toTime = timestamp + dayMs;
              
              setIsFetchingTicks(true);
              try {
              console.log('[App] 📅 Fetching initial 1-day tick window...');
              console.log(`[App]   From: ${new Date(timestamp).toISOString()}`);
              console.log(`[App]   To: ${new Date(toTime).toISOString()}`);
              
              // Timeout promise wrapper (2 minutes)
              const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("Tick fetch timeout")), 120000));
              
              const ticks = await Promise.race([
                  fetchTickData(liveTicker, timestamp, toTime),
                  timeout
              ]) as TickData[];

              if (ticks && ticks.length > 0) {
                  const sortedTicks = ticks.sort((a,b) => a.time - b.time);
                  
                  // Log tick buffer summary and check for future ticks
                  const firstTick = sortedTicks[0];
                  const lastTick = sortedTicks[sortedTicks.length - 1];
                  const currentTime = Date.now();
                  
                  console.log(`[App] Loaded ${sortedTicks.length} ticks for replay:`, {
                      firstTickTime: new Date(firstTick.time).toLocaleString(),
                      lastTickTime: new Date(lastTick.time).toLocaleString(),
                      replayStartTime: new Date(timestamp).toLocaleString(),
                      currentSystemTime: new Date(currentTime).toLocaleString()
                  });
                  
                  // Check for any ticks with future timestamps (beyond current system time) - this would be a real error
                  const futureTicks = sortedTicks.filter(t => t.time > currentTime);
                  if (futureTicks.length > 0) {
                      console.error('[App] ⚠️ ERROR: Found ticks with FUTURE timestamps (beyond current time)!', {
                          count: futureTicks.length,
                          firstFutureTick: new Date(futureTicks[0].time).toLocaleString(),
                          lastFutureTick: new Date(futureTicks[futureTicks.length - 1].time).toLocaleString(),
                          note: 'This indicates invalid data from the source'
                      });
                  }
                  
                  // Info: Ticks ahead of replay start are normal (we fetch full candle range for caching)
                  const weekAhead = timestamp + (7 * 24 * 60 * 60 * 1000);
                  const ticksAhead = sortedTicks.filter(t => t.time > weekAhead);
                  if (ticksAhead.length > 0) {
                      console.log('[App] ℹ️ Loaded ticks beyond replay start (normal - for full session coverage):', {
                          count: ticksAhead.length,
                          replayStart: new Date(timestamp).toLocaleString(),
                          ticksExtendTo: new Date(sortedTicks[sortedTicks.length - 1].time).toLocaleString(),
                          note: 'These ticks will be used as replay progresses'
                      });
                  }
                  
                  console.log(`[App] Setting tick buffer with ${sortedTicks.length} ticks`);
                  
                  // Find the starting tick index - skip past all ticks before the replay start time
                  // This prevents processing hundreds of thousands of old ticks on the first step
                  let startingTickIndex = 0;
                  for (let i = 0; i < sortedTicks.length; i++) {
                      if (sortedTicks[i].time >= timestamp) {
                          startingTickIndex = i;
                          break;
                      }
                  }
                  
                  console.log(`[App] 🎯 Starting tick replay at index ${startingTickIndex} of ${sortedTicks.length}`, {
                      firstTickTime: new Date(sortedTicks[0].time).toLocaleString(),
                      startingTickTime: startingTickIndex < sortedTicks.length ? new Date(sortedTicks[startingTickIndex].time).toLocaleString() : 'N/A',
                      replayStartTime: new Date(timestamp).toLocaleString(),
                      skippedTicks: startingTickIndex
                  });
                  
                  setTickBuffer(sortedTicks);
                  
                  // Update replay state with the correct starting tick index
                  setReplayState(prev => ({
                      ...prev,
                      tickIndex: startingTickIndex
                  }));
                  
                  console.log('[App] Tick buffer set, replay controls should now be enabled');
                  
                  // Immediately save session with tick buffer to avoid refetch on refresh (async, don't block)
                  const initialSession = {
                      id: `replay_${liveTicker}_${timeframe}`,
                      ticker: liveTicker,
                      timeframe: timeframe,
                      startTime: timestamp,
                      trades: virtualTrades,
                      balance: virtualBalance,
                      initialBalance: 100000,
                      timestamp: Date.now(),
                      replayState: {
                          currentIndex: index,
                          tickIndex: startingTickIndex,
                          replayTime: candles[index].time,
                          displayTime: candles[index].time,
                          isPlaying: false,
                          speed: 1000
                      },
                      currentCandles: candles.slice(0, index + 1),
                      fullHistory: [...candles],
                      tickBuffer: sortedTicks
                  };
                  saveReplaySession(initialSession).then(() => {
                      console.log('[App] Initial replay session saved with tick buffer');
                  }).catch(err => {
                      console.error('[App] Failed to save initial replay session:', err);
                  });
              } else {
                  console.log('[App] No ticks available, falling back to candle-only replay.');
                  setTickBuffer([]);
              }
              } catch (e) {
                  console.error("[App] Tick fetch failed/timed out:", e);
                  setTickBuffer([]);
              } finally {
                  setIsFetchingTicks(false);
              }
          } else {
              // Candle replay mode - no tick data needed
              console.log('[App] Candle replay mode - skipping tick download');
              setTickBuffer([]);
              setIsFetchingTicks(false);
          }

      } else {
          console.warn('[App] No candle found after timestamp');
      }
  };

  // 3. Step Forward (Time-Based)
  const stepReplayForward = useCallback(() => {
      console.log('[App] stepReplayForward called', { replayMode, fullHistoryLength: fullHistory.length, layoutType });
      if (replayMode !== 'active' || fullHistory.length === 0) return;
      
      // Candle Mode: Simple candle-by-candle replay
      if (replayType === 'candle') {
          // In multi-layout mode, use the lowest timeframe to determine step size
          // Each panel uses its own full history data
          if (isMultiLayout(layoutType)) {
              const { timeframe: lowestTf, ms: lowestTfMs } = getLowestTimeframe(panels);
              const mainTfMs = TIMEFRAME_MS[timeframe as keyof typeof TIMEFRAME_MS] || 300000;
              
              const currentReplayTime = replayState.replayTime || (candles.length > 0 ? candles[candles.length - 1].time : 0);
              const nextReplayTime = currentReplayTime + lowestTfMs;
              
              // In multi-layout, fullHistory comes from panel data with the lowest timeframe
              // So we should use lowestTfMs for completion time calculation
              console.log('[App] Multi-layout candle replay step:', {
                  lowestTf,
                  lowestTfMs,
                  mainTfMs,
                  currentReplayTime: new Date(currentReplayTime).toLocaleString(),
                  nextReplayTime: new Date(nextReplayTime).toLocaleString()
              });
              
              // Find candles from main fullHistory for the main chart
              // In multi-layout, fullHistory contains candles from the lowest timeframe panel
              // Use lowestTfMs for completion time since that matches the data source
              const mainCandlesToProcess = fullHistory.filter(c => {
                  const candleCompletionTime = c.time + lowestTfMs;
                  return candleCompletionTime > currentReplayTime && candleCompletionTime <= nextReplayTime;
              });
              
              // Debug: Log fullHistory range to understand why no candles are found
              if (fullHistory.length > 0) {
                  const firstTime = fullHistory[0].time;
                  const lastTime = fullHistory[fullHistory.length - 1].time;
                  // Find a sample candle near the replay time for debugging
                  const sampleCandle = fullHistory.find(c => c.time >= currentReplayTime - lowestTfMs * 2);
                  console.log('[App] fullHistory range:', {
                      first: new Date(firstTime).toLocaleString(),
                      last: new Date(lastTime).toLocaleString(),
                      count: fullHistory.length,
                      lowestTfMs,
                      currentReplayTime: new Date(currentReplayTime).toLocaleString(),
                      nextReplayTime: new Date(nextReplayTime).toLocaleString(),
                      lookingForCompletionBetween: `${new Date(currentReplayTime).toLocaleString()} < completion <= ${new Date(nextReplayTime).toLocaleString()}`,
                      sampleCandleNearReplay: sampleCandle ? {
                          time: new Date(sampleCandle.time).toLocaleString(),
                          completionTime: new Date(sampleCandle.time + lowestTfMs).toLocaleString()
                      } : 'none',
                      found: mainCandlesToProcess.length
                  });
              }
              
              if (mainCandlesToProcess.length > 0) {
                  console.log('[App] Adding', mainCandlesToProcess.length, 'candles to main chart:', mainCandlesToProcess.map(c => new Date(c.time).toLocaleString()));
                  setCandles(prev => {
                      const newCandles = [...prev, ...mainCandlesToProcess];
                      console.log('[App] Main candles updated:', prev.length, '->', newCandles.length);
                      return newCandles;
                  });
              }
              
              // Update each panel using its own full history
              console.log('[App] Panel full history size:', panelFullHistory.size, 'panels:', panels.map(p => p.id));
              
              // Get the current price from the main candles being added (for syncing higher TF panels)
              const currentPrice = mainCandlesToProcess.length > 0 
                  ? mainCandlesToProcess[mainCandlesToProcess.length - 1].close 
                  : (candles.length > 0 ? candles[candles.length - 1].close : null);
              
              setPanelDataMap(prevMap => {
                  const newMap = new Map(prevMap);
                  
                  for (const panel of panels) {
                      const panelHistory = panelFullHistory.get(panel.id);
                      if (!panelHistory || panelHistory.length === 0) {
                          console.log(`[App] Panel ${panel.id} has no history data`);
                          continue;
                      }
                      
                      const currentPanelCandles = [...(newMap.get(panel.id) || [])];
                      const panelTfMs = TIMEFRAME_MS[panel.timeframe as keyof typeof TIMEFRAME_MS] || 300000;
                      
                      // Add candles when they COMPLETE (for all timeframes)
                      // A candle completes at: candle.time + timeframe duration
                      // This ensures candles only appear when their timeframe period is complete
                      const panelCandlesToAdd = panelHistory.filter(c => {
                          const candleCompletionTime = c.time + panelTfMs;
                          return candleCompletionTime > currentReplayTime && candleCompletionTime <= nextReplayTime;
                      });
                      
                      console.log(`[App] Panel ${panel.id}: history=${panelHistory.length}, current=${currentPanelCandles.length}, toAdd=${panelCandlesToAdd.length}`);
                      
                      if (panelCandlesToAdd.length > 0) {
                          // New candle(s) to add for this panel's timeframe
                          const newPanelCandles = [...currentPanelCandles, ...panelCandlesToAdd];
                          console.log(`[App] Panel ${panel.id} updated: ${currentPanelCandles.length} -> ${newPanelCandles.length} candles`);
                          newMap.set(panel.id, newPanelCandles);
                      } else if (currentPrice !== null && currentPanelCandles.length > 0 && panelTfMs > lowestTfMs) {
                          // Higher timeframe panel with no new candle - update the last candle's close/high/low
                          // to reflect current price (simulating in-progress candle)
                          const lastCandle = { ...currentPanelCandles[currentPanelCandles.length - 1] };
                          lastCandle.close = currentPrice;
                          lastCandle.high = Math.max(lastCandle.high, currentPrice);
                          lastCandle.low = Math.min(lastCandle.low, currentPrice);
                          currentPanelCandles[currentPanelCandles.length - 1] = lastCandle;
                          newMap.set(panel.id, currentPanelCandles);
                      }
                  }
                  
                  return newMap;
              });
              
              // Check if we've reached the end (compare against last candle's COMPLETION time)
              const lastHistoryTime = fullHistory[fullHistory.length - 1]?.time || 0;
              const lastHistoryCompletionTime = lastHistoryTime + lowestTfMs;
              if (nextReplayTime > lastHistoryCompletionTime && mainCandlesToProcess.length === 0) {
                  console.log('[App] Reached end of replay');
                  setReplayState(prev => ({ ...prev, isPlaying: false }));
                  return;
              }
              
              // For completion-time based logic, replayTime should advance to nextReplayTime
              // This ensures the next step looks for candles completing AFTER this point
              const lastCandleTime = mainCandlesToProcess.length > 0 
                  ? mainCandlesToProcess[mainCandlesToProcess.length - 1].time 
                  : null;
              const newIndex = lastCandleTime !== null
                  ? fullHistory.findIndex(c => c.time === lastCandleTime)
                  : replayState.currentIndex;
              
              // Display time shows the last candle's start time, but replayTime advances to nextReplayTime
              // This is critical for completion-time based stepping to work correctly
              setReplayState(prev => ({
                  ...prev,
                  currentIndex: newIndex >= 0 ? newIndex : prev.currentIndex,
                  replayTime: nextReplayTime, // Always advance to next step time
                  displayTime: lastCandleTime ?? nextReplayTime // Show last candle time for display
              }));
              return;
          }
          
          // Single layout: simple candle-by-candle replay
          const nextIndex = replayState.currentIndex + 1;
          if (nextIndex >= fullHistory.length) {
              console.log('[App] Reached end of replay');
              setReplayState(prev => ({ ...prev, isPlaying: false }));
              return;
          }
          
          const nextCandle = fullHistory[nextIndex];
          setCandles(prev => [...prev, nextCandle]);
          
          setReplayState(prev => ({
              ...prev,
              currentIndex: nextIndex,
              replayTime: nextCandle.time,
              displayTime: nextCandle.time
          }));
          return;
      }
      
      // Tick Mode: Hybrid tick-based replay
      // Even if no ticks, we still need to create candles based on time
      const lastCandleTime = candles[candles.length - 1]?.time;
      if (!lastCandleTime) return; // No candles to work with
      
      // Use time-based replay if we have ticks OR if replayTime has been initialized (> 0)
      if (tickBuffer.length > 0 || replayState.replayTime > 0) {
          // 1. Advance Market Time based on speed setting
          // Speed values: 1000ms (1x), 500ms (2x), 200ms (5x), 50ms (10x) - this is the INTERVAL between steps
          // We want: 1x = real-time (1 sec market per 1 sec real), 10x = 10 sec market per 1 sec real
          // Since interval is faster at higher speeds, we keep timeStep constant at 1 second
          // The speedup comes from more frequent steps (50ms interval = 20 steps/sec at 10x)
          const timeStep = 1000; // Always advance 1 second of market time per step
          
          // CRITICAL: Base replay time on the last candle's time, NOT system time
          // replayState.replayTime tracks how far into the current candle we are
          // If replayTime is way off (e.g., current system time), reset it to lastCandleTime
          let baseReplayTime = replayState.replayTime > 0 ? replayState.replayTime : lastCandleTime;
          
          // Sanity check: if baseReplayTime is more than 2 candle periods ahead of lastCandleTime, reset it
          // This prevents jumps to current system time
          const tfMs = timeframe === '1m' ? 60000 : timeframe === '5m' ? 300000 : 
                       timeframe === '15m' ? 900000 : timeframe === '30m' ? 1800000 :
                       timeframe === '1h' ? 3600000 : timeframe === '4h' ? 14400000 : 
                       timeframe === '1d' ? 86400000 : 3600000;
          const maxAllowedTime = lastCandleTime + (tfMs * 2);
          
          if (baseReplayTime > maxAllowedTime) {
              console.warn('[App] Replay time was invalid:', new Date(baseReplayTime).toLocaleString(), 
                          'vs lastCandle:', new Date(lastCandleTime).toLocaleString(), '- resetting');
              baseReplayTime = lastCandleTime;
          }
          
          const nextReplayTime = baseReplayTime + timeStep;
          
          let currentTickIndex = replayState.tickIndex;
          let currentCandles = [...candles];
          let processedCount = 0;

          // 2. Process ALL ticks that occurred up to nextReplayTime
          const startTickIndex = currentTickIndex;
          
          // Check if we need to fetch next day's tick data (rolling window)
          const remainingTicks = tickBuffer.length - currentTickIndex;
          const bufferThreshold = 100; // Fetch next window when less than 100 ticks remain
          
          if (remainingTicks < bufferThreshold && !isFetchingTicks) {
              const lastTickTime = tickBuffer[tickBuffer.length - 1]?.time;
              if (lastTickTime) {
                  const dayMs = 24 * 60 * 60 * 1000;
                  const nextWindowStart = lastTickTime;
                  const nextWindowEnd = lastTickTime + dayMs;
                  
                  console.log('[App] 📅 Fetching next 1-day tick window...');
                  console.log(`[App]   Remaining ticks: ${remainingTicks}`);
                  console.log(`[App]   From: ${new Date(nextWindowStart).toISOString()}`);
                  console.log(`[App]   To: ${new Date(nextWindowEnd).toISOString()}`);
                  
                  setIsFetchingTicks(true);
                  fetchTickData(liveTicker, nextWindowStart, nextWindowEnd)
                      .then(newTicks => {
                          if (newTicks && newTicks.length > 0) {
                              const sortedNewTicks = newTicks.sort((a, b) => a.time - b.time);
                              console.log(`[App] ✅ Fetched ${sortedNewTicks.length} ticks for next window`);
                              
                              // Append to existing buffer
                              setTickBuffer(prev => [...prev, ...sortedNewTicks]);
                          } else {
                              console.log('[App] ⚠️ No more tick data available');
                          }
                      })
                      .catch(err => console.error('[App] Failed to fetch next tick window:', err))
                      .finally(() => setIsFetchingTicks(false));
              }
          }
          
          // Collect all ticks to process in this time window
          const ticksToProcess: Array<{ tick: TickData; index: number }> = [];
          while (currentTickIndex < tickBuffer.length) {
              const tick = tickBuffer[currentTickIndex];
              
              if (tick.time > nextReplayTime) {
                  // Log if we're skipping ticks because they're in the future
                  if (currentTickIndex === startTickIndex && currentTickIndex < tickBuffer.length - 1) {
                      console.log('[App] ⏭️  No ticks to process - next tick is in future:', {
                          nextReplayTime: new Date(nextReplayTime).toLocaleString(),
                          nextTickTime: new Date(tick.time).toLocaleString(),
                          timeDiff: `${Math.round((tick.time - nextReplayTime) / 1000)}s`
                      });
                  }
                  break; // Future tick, stop processing
              }
              
              // SANITY CHECK: Skip ticks that are way in the future (e.g., current system time)
              // This prevents corrupted tick data from creating candles at wrong times
              if (tick.time > lastCandleTime + (tfMs * 100)) {
                  console.error('[App] ⚠️ INVALID TICK DETECTED - Future timestamp!', {
                      tickTime: new Date(tick.time).toLocaleString(),
                      tickTimestamp: tick.time,
                      lastCandleTime: new Date(lastCandleTime).toLocaleString(),
                      lastCandleTimestamp: lastCandleTime,
                      tickIndex: currentTickIndex,
                      tickPrice: tick.price,
                      tickVolume: tick.volume,
                      difference: `${Math.round((tick.time - lastCandleTime) / 60000)} minutes ahead`
                  });
                  currentTickIndex++;
                  continue;
              }

              ticksToProcess.push({ tick, index: currentTickIndex });
              currentTickIndex++;
          }
          
          // Process all ticks immediately (no micro-delays to avoid timing issues)
          if (ticksToProcess.length > 0) {
              console.log(`[App] 🎬 Processing ${ticksToProcess.length} ticks immediately`);
              
              // Process all ticks in a single batch update
              setCandles(prevCandles => {
                  let updatedCandles = prevCandles;
                  
                  for (let i = 0; i < ticksToProcess.length; i++) {
                      const tickData = ticksToProcess[i];
                      const result = processReplayTick(
                          updatedCandles,
                          { time: tickData.tick.time, price: tickData.tick.price, volume: tickData.tick.volume },
                          timeframe
                      );
                      updatedCandles = result.updatedCandles;
                      
                      // Log first few for debugging
                      if (i < 5) {
                          const lastCandle = updatedCandles[updatedCandles.length - 1];
                          console.log(`[App] 📊 Tick ${i + 1}/${ticksToProcess.length}:`, {
                              tickPrice: tickData.tick.price,
                              candleClose: lastCandle.close,
                              candleVolume: lastCandle.volume
                          });
                      }
                  }
                  
                  return updatedCandles;
              });
              
              // Update multi-layout panels with tick data
              if (isMultiLayout(layoutType)) {
                  setPanelDataMap(prevMap => updatePanelsForReplay(
                      { layoutType, panels, panelDataMap: prevMap },
                      { replayTime: nextReplayTime, ticks: ticksToProcess as IndexedTick[] }
                  ));
              }
              
              // Update replay state immediately after processing
              setReplayState(prev => ({ 
                  ...prev, 
                  tickIndex: ticksToProcess[ticksToProcess.length - 1].index + 1, 
                  replayTime: nextReplayTime,
                  displayTime: nextReplayTime
              }));
              
              // Update current state for immediate use
              processedCount = ticksToProcess.length;
              currentTickIndex = ticksToProcess[ticksToProcess.length - 1].index + 1;
              
              // Process all ticks immediately for currentCandles (for candle creation check)
              for (const tickData of ticksToProcess) {
                  const { updatedCandles } = processReplayTick(
                      currentCandles,
                      { time: tickData.tick.time, price: tickData.tick.price, volume: tickData.tick.volume },
                      timeframe
                  );
                  currentCandles = updatedCandles;
              }
          }

          // 3. Check if we need to create a new candle due to time advancement
          // Even if no ticks were processed, if nextReplayTime crosses into a new candle period,
          // we should create a new candle (using the last known price)
          // Note: tfMs is already defined above for sanity check
          
          const lastCandle = currentCandles[currentCandles.length - 1];
          // Calculate the NEXT candle time based on the LAST candle's time, not nextReplayTime
          const expectedNextCandleTime = lastCandle.time + tfMs;
          
          // Debug: Log every 10 seconds to track progress
          if (Math.floor(nextReplayTime / 10000) !== Math.floor((nextReplayTime - timeStep) / 10000)) {
              console.log('[App] Replay Progress:', {
                  nextReplayTime: new Date(nextReplayTime).toLocaleString(),
                  lastCandleTime: new Date(lastCandle.time).toLocaleString(),
                  expectedNextCandleTime: new Date(expectedNextCandleTime).toLocaleString(),
                  timeUntilNewCandle: expectedNextCandleTime - nextReplayTime
              });
          }
          
          // Log the comparison for debugging
          console.log('[App] Candle creation check:', {
              nextReplayTime: nextReplayTime,
              expectedNextCandleTime: expectedNextCandleTime,
              shouldCreate: nextReplayTime >= expectedNextCandleTime,
              nextReplayTimeStr: new Date(nextReplayTime).toLocaleString(),
              expectedNextCandleTimeStr: new Date(expectedNextCandleTime).toLocaleString(),
              lastCandleTimeStr: new Date(lastCandle.time).toLocaleString()
          });
          
          if (nextReplayTime >= expectedNextCandleTime) {
              // Time has crossed into a new candle period - create new candle
              // Use the expected next candle time (lastCandle.time + tfMs), not nextReplayTime
              const lastPrice = lastCandle.close;
              const newCandle: CandleData = {
                  time: expectedNextCandleTime,
                  open: lastPrice,
                  high: lastPrice,
                  low: lastPrice,
                  close: lastPrice,
                  volume: 0,
                  dateStr: new Date(expectedNextCandleTime).toLocaleTimeString()
              };
              currentCandles = [...currentCandles, newCandle];
              console.log('[App] ✅ New candle created at:', new Date(expectedNextCandleTime).toLocaleString());
          }

          // 4. Update State (only if no ticks were processed)
          if (ticksToProcess.length === 0) {
              setCandles(currentCandles);
              setReplayState(prev => ({ 
                  ...prev, 
                  tickIndex: currentTickIndex, 
                  replayTime: nextReplayTime,
                  displayTime: nextReplayTime
              }));
          }
          // Note: If ticks were processed, state updates already happened above
          
          // Debug Replay Logic
          if (processedCount > 0) {
              const debugLastCandle = currentCandles[currentCandles.length - 1];
              console.log('[App] Replay Step (Time-Based):', {
                  marketTime: new Date(nextReplayTime).toLocaleTimeString(),
                  processedTicks: processedCount,
                  lastCandleTime: new Date(debugLastCandle.time).toLocaleTimeString(),
                  close: debugLastCandle.close
              });
          }
          return;
      }

      // Fallback: Standard Candle-by-Candle (if no ticks)
      const nextIndex = replayState.currentIndex + 1;
      if (nextIndex >= fullHistory.length) {
          setReplayState(prev => ({ ...prev, isPlaying: false })); // End of data
          return;
      }
      setCandles(fullHistory.slice(0, nextIndex + 1));
      setReplayState(prev => ({ 
          ...prev, 
          currentIndex: nextIndex,
          replayTime: fullHistory[nextIndex].time
      }));
  }, [replayMode, fullHistory, tickBuffer, candles, timeframe, replayState, layoutType, panels]);

  // 4. Play/Pause Loop
  useEffect(() => {
      let interval: NodeJS.Timeout;
      const canPlay = replayMode === 'active' && replayState.isPlaying && 
                      (replayType === 'candle' || tickBuffer.length > 0);
      
      if (canPlay) {
          console.log('[App] Starting replay interval with speed:', replayState.speed, 'ms', 'mode:', replayType);
          interval = setInterval(stepReplayForward, replayState.speed);
      } else if (replayMode === 'active' && replayState.isPlaying && replayType === 'tick' && tickBuffer.length === 0) {
          console.warn('[App] Cannot start tick replay: No tick data available');
          // Auto-pause if no ticks in tick mode
          setReplayState(prev => ({ ...prev, isPlaying: false }));
      }
      return () => clearInterval(interval);
  }, [replayMode, replayState.isPlaying, replayState.speed, stepReplayForward, tickBuffer.length, replayType]);

  // --- VIRTUAL TRADING LOGIC ---
  const getTradeTimestamp = () => {
      const lastCandleTime = candles[candles.length - 1]?.time;
      // When replay is active, align trade timestamps to the replay display time shown in the UI
      if (replayMode === 'active' && replayState.displayTime > 0) {
          return replayState.displayTime;
      }
      return lastCandleTime;
  };

  const handleBuy = (quantity: number = 100) => {
      if (!currentPrice) return;
      const trade: import('./types').VirtualTrade = {
          id: Date.now().toString(),
          symbol: liveTicker,
          type: 'BUY',
          entryPrice: currentPrice,
          quantity: quantity, 
          entryTime: getTradeTimestamp(),
          status: 'OPEN'
      };
      setVirtualTrades(prev => [...prev, trade]);
  };

  const handleSell = (quantity: number = 100) => {
      if (!currentPrice) return;
      const trade: import('./types').VirtualTrade = {
          id: Date.now().toString(),
          symbol: liveTicker,
          type: 'SELL',
          entryPrice: currentPrice,
          quantity: quantity,
          entryTime: getTradeTimestamp(),
          status: 'OPEN'
      };
      setVirtualTrades(prev => [...prev, trade]);
  };

  const handleClosePosition = (id: string) => {
      setVirtualTrades(prev => prev.map(t => {
          if (t.id === id && t.status === 'OPEN') {
              const pnl = (currentPrice - t.entryPrice) * t.quantity * (t.type === 'BUY' ? 1 : -1);
              setVirtualBalance(b => b + pnl); // Realize PnL
              return { ...t, status: 'CLOSED', exitPrice: currentPrice, exitTime: getTradeTimestamp(), pnl };
          }
          return t;
      }));
  };

  // Auto-save replay trading session when trades or balance change during replay (localStorage-only)
  useEffect(() => {
    if (replayMode === 'active' && liveTicker && timeframe) {
      const saveSessionLocal = async () => {
        const session = {
          id: `replay_${liveTicker}_${timeframe}`,
          ticker: liveTicker,
          timeframe: timeframe,
          startTime: replayState.replayTime || candles[0]?.time || Date.now(),
          trades: virtualTrades,
          balance: virtualBalance,
          initialBalance: 100000,
          timestamp: Date.now(),
          replayState: {
            currentIndex: replayState.currentIndex,
            tickIndex: replayState.tickIndex,
            replayTime: replayState.replayTime,
            displayTime: replayState.displayTime,
            isPlaying: replayState.isPlaying,
            speed: replayState.speed
          },
          currentCandles: candles,
          fullHistory: fullHistory,
          tickBuffer: tickBuffer
        };
        console.log('[App] Saving replay session locally with:', {
          ticker: liveTicker,
          timeframe,
          tickBufferSize: tickBuffer.length,
          tradesCount: virtualTrades.length
        });
        await saveReplaySession(session);
        console.log('[App] Replay session auto-saved (localStorage)');
      };
      saveSessionLocal();
    }
  }, [virtualTrades, virtualBalance, replayMode, liveTicker, timeframe, replayState, candles, fullHistory, tickBuffer]);

  // Restore replay session on mount if one exists (localStorage-only)
  useEffect(() => {
    const restoreReplaySession = async () => {
      if (!isAuthLoaded) return;
      if (!isSignedIn) return;
      if (didRestoreReplaySessionRef.current) return;

      const savedSession = await getReplaySession();
      
      if (savedSession && savedSession.replayState && savedSession.currentCandles && savedSession.fullHistory) {
        didRestoreReplaySessionRef.current = true;
        console.log('[App] Restoring full replay session from last state');
        
        // Restore ticker and timeframe
        setLiveTicker(savedSession.ticker);
        setTickerInput(savedSession.ticker); // Sync input field
        setTimeframe(savedSession.timeframe as Timeframe);
        setMode(AppMode.LIVE);
        
        // Restore chart data
        setCandles(savedSession.currentCandles);
        setFullHistory(savedSession.fullHistory);
        
        // Restore trades and balance
        setVirtualTrades(savedSession.trades);
        setVirtualBalance(savedSession.balance);
        
        // Restore replay state
        setReplayMode('active');
        setReplayState({
          currentIndex: savedSession.replayState.currentIndex,
          tickIndex: savedSession.replayState.tickIndex,
          replayTime: savedSession.replayState.replayTime,
          displayTime: savedSession.replayState.displayTime,
          isPlaying: false, // Don't auto-play on restore
          speed: savedSession.replayState.speed
        });
        
        // Switch to replay tab
        setActiveTab('replay');
        setIsBottomPanelExpanded(true);
        
        // Restore tick buffer if available, otherwise refetch
        console.log('[App] Checking tick buffer in saved session:', {
          hasTickBuffer: !!savedSession.tickBuffer,
          tickCount: savedSession.tickBuffer?.length || 0
        });
        
        if (savedSession.tickBuffer && savedSession.tickBuffer.length > 0) {
          console.log(`[App] ✅ Restoring ${savedSession.tickBuffer.length} ticks from saved session (NO DOWNLOAD NEEDED)`);
          setTickBuffer(savedSession.tickBuffer);
          console.log('[App] Replay session fully restored from cache');
        } else {
          // Fallback: Refetch ticks using 1-day window from replay start
          console.log('[App] ⚠️ No cached ticks found, fetching initial 1-day window...');
          const fromTime = savedSession.replayState.replayTime || savedSession.currentCandles[0]?.time;
          const dayMs = 24 * 60 * 60 * 1000;
          const toTime = fromTime + dayMs;
          
          console.log(`[App]   From: ${new Date(fromTime).toISOString()}`);
          console.log(`[App]   To: ${new Date(toTime).toISOString()}`);
          
          setIsFetchingTicks(true);
          try {
            const timeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error("Tick fetch timeout")), 120000)
            );
            
            const ticks = await Promise.race([
              fetchTickData(savedSession.ticker, fromTime, toTime),
              timeout
            ]) as TickData[];

            if (ticks && ticks.length > 0) {
              const sortedTicks = ticks.sort((a, b) => a.time - b.time);
              setTickBuffer(sortedTicks);
              console.log(`[App] ✅ Loaded ${sortedTicks.length} ticks for restored replay (1-day window)`);
            } else {
              console.log('[App] No ticks available for restored replay');
              setTickBuffer([]);
            }
          } catch (e) {
            console.error("[App] Tick fetch failed for restored replay:", e);
            setTickBuffer([]);
          } finally {
            setIsFetchingTicks(false);
          }
          
          console.log('[App] Replay session fully restored');
          setIsRestoringSession(false);
        }
      } else {
        // No replay session to restore, clear restoring state if we were waiting for auth
        if (isAuthLoaded && isSignedIn) {
          setIsRestoringSession(false);
        }
      }
    };
    restoreReplaySession();
  }, [isAuthLoaded, isSignedIn]);

  // Initial Load of Saved Sessions (localStorage-only)
  useEffect(() => {
      const loadSessions = async () => {
          try {
              const sessions = await getSavedSessions();
              setSavedSessions(sessions);

              // If there is no replay session restored, auto-restore the most recent LIVE session
              // so prior analysis/chat is available immediately after refresh.
              if (false && !didRestoreReplaySessionRef.current) {
                  const latestLiveSession = sessions
                      .filter(s => s.mode === AppMode.LIVE)
                      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0];

                  if (latestLiveSession) {
                      await handleLoadSession(latestLiveSession);
                  }
              }
          } finally {
              // Only set isRestoringSession to false if no replay session exists
              // Use synchronous check to avoid race condition with async replay restoration
              if (!hasReplaySession()) {
                  setIsRestoringSession(false);
              }
          }
      };
      loadSessions();
  }, []);

  // Unified Auto-save Effect (Chat & Analysis History)
  useEffect(() => {
    if (!currentSessionId) return;

    // Debounce save to prevent excessive writes
    const timer = setTimeout(async () => {
        const session = savedSessions.find(s => s.id === currentSessionId);
        if (!session) return;

        const sessionChat = JSON.stringify(session.chatHistory || []);
        const stateChat = JSON.stringify(chatMessages);
        
        const sessionAnalysis = JSON.stringify(session.analysisHistory || []);
        const stateAnalysis = JSON.stringify(analysisHistory);

        const hasChatChanged = sessionChat !== stateChat;
        const hasAnalysisChanged = sessionAnalysis !== stateAnalysis;

        if (hasChatChanged || hasAnalysisChanged) {
            const updatedSession: SavedSession = { 
                ...session, 
                chatHistory: chatMessages,
                analysisHistory: analysisHistory,
                // Update the 'latest' analysis reference if history changed
                analysis: analysisHistory.length > 0 ? analysisHistory[0] : session.analysis 
            };
            
            const updatedList = await saveSession(updatedSession);
            setSavedSessions(updatedList);
            console.debug("Auto-saved session data (localStorage)");
        }
    }, 2000); // 2s debounce

    return () => clearTimeout(timer);
  }, [chatMessages, analysisHistory, currentSessionId, savedSessions]);

  // Keyboard shortcut: Shift+R to force refresh (bypass cache)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === 'R' && mode === AppMode.LIVE) {
        e.preventDefault();
        debug.log('[App] 🔄 Force refresh triggered (Shift+R)');
        handleFetchLiveData(undefined, liveTicker, timeframe, true);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [mode, liveTicker, timeframe]);

  const handleImageSelected = (base64: string, mimeType: string) => {
    setSelectedImage(base64);
    setImageMimeType(mimeType);
    setAnalysisHistory([]);
    setAnalysisId('');
    setError(null);
    setChatMessages([]);
    setCurrentSessionId(null);
  };

  const handleAnalyzeImage = async () => {
    if (!selectedImage) return;
    setIsLoading(true);
    setError(null);
    try {
      const rawBase64 = stripBase64Prefix(selectedImage);
      const result = await analyzeChartImage(rawBase64, imageMimeType);
      // Add timestamp to result
      result.timestamp = Date.now();
      
      const newHistory = [result];

      // --- AUTO SAVE START ---
      const newId = Date.now().toString();
      const newSession: SavedSession = {
          id: newId,
          timestamp: Date.now(),
          mode: AppMode.UPLOAD,
          title: `Chart Analysis ${formatTimestamp(Date.now())}`,
          analysis: result, // Latest for preview
          analysisHistory: newHistory,
          chatHistory: [],
          imageBase64: selectedImage,
          imageMimeType: imageMimeType
      };
      
      const updated = await saveSession(newSession);
      setSavedSessions(updated);
      // --- AUTO SAVE END ---

      setAnalysisHistory(newHistory);
      setAnalysisId(newId);
      setCurrentSessionId(newId);
      setChatMessages([]); 
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred during analysis.');
    } finally {
      setIsLoading(false);
    }
  };

  // Re-Analyze Function
  const handleReAnalyze = async () => {
    setIsLoading(true);
    setError(null);
    // Expand bottom panel and switch to Analysis tab
    setIsBottomPanelExpanded(true);
    setBottomPanelHeight(300);
    setActiveTab('analysis');

    try {
      let result: AnalysisResult;
      // Context from previous analysis to maintain continuity
      const previousContext = analysis ? analysis.technicalAnalysis : undefined;

      if (mode === AppMode.UPLOAD && selectedImage) {
        const rawBase64 = stripBase64Prefix(selectedImage);
        result = await analyzeChartImage(rawBase64, imageMimeType, previousContext);
        result.mode = AnalysisMode.UPLOAD;
      } else if (mode === AppMode.LIVE && liveTicker) {
        debug.log('[App] 📸 Re-analyzing with chart screenshot...');

        let chartImageData: string | null = null;

        chartImageData = await (async (): Promise<string | null> => {
          // Wait for chart to render
          await new Promise(resolve => setTimeout(resolve, 300));

          const chartContainers = Array.from(document.querySelectorAll('[data-testid="live-chart-container"]')) as HTMLElement[];
          const visible = chartContainers
            .map(el => ({ el, rect: el.getBoundingClientRect() }))
            .filter(x => x.rect.width > 0 && x.rect.height > 0)
            .sort((a, b) => (b.rect.width * b.rect.height) - (a.rect.width * a.rect.height));

          let chartContainer = visible[0]?.el || chartContainers[0];

          console.log('[App][ReAnalyze][Capture] containers:', {
            count: chartContainers.length,
            visibleCount: visible.length
          });

          if (chartContainer) {
            // Go up 2 levels to get the full chart panel with header
            if (chartContainer.parentElement?.parentElement) {
              chartContainer = chartContainer.parentElement.parentElement;
              debug.log('[App] Using grandparent container (should include header)');
            } else if (chartContainer.parentElement) {
              chartContainer = chartContainer.parentElement;
              debug.log('[App] Using parent container');
            }
          }

          if (!chartContainer) return null;

          const rect = chartContainer.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) {
            console.error('[App][ReAnalyze][Capture] chartContainer has zero dimensions:', rect);
            return null;
          }

          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(chartContainer, {
            backgroundColor: '#1e1e1e',
            scale: 1,
            logging: false,
            useCORS: true,
            allowTaint: true,
            width: rect.width,
            height: rect.height,
            x: 0,
            y: 0
          });

          const dataUrl = canvas.toDataURL('image/png');
          const rawBase64Candidate = stripBase64Prefix(dataUrl);

          console.log('[App][ReAnalyze][Capture] canvas:', {
            width: canvas.width,
            height: canvas.height,
            dataUrlLength: dataUrl?.length || 0
          });
          console.log('[App][ReAnalyze][Capture] base64:', {
            rawLength: rawBase64Candidate?.length || 0,
            head: rawBase64Candidate ? rawBase64Candidate.slice(0, 24) : null,
            tail: rawBase64Candidate ? rawBase64Candidate.slice(-24) : null,
          });

          if (rawBase64Candidate && rawBase64Candidate.length > 100 && !dataUrl.endsWith('data:,')) {
            setLatestChartScreenshot(dataUrl);
            return dataUrl;
          }

          console.error('[App] Screenshot capture returned empty or invalid data');
          return null;
        })().catch((captureError) => {
          console.error('[App] Failed to capture chart for re-analysis:', captureError);
          console.error('[App][ReAnalyze][Capture] error details:', {
            message: captureError instanceof Error ? captureError.message : String(captureError),
            stack: captureError instanceof Error ? captureError.stack : undefined
          });
          return null;
        });

        if (chartImageData) {
          const rawBase64 = stripBase64Prefix(chartImageData);
          const promptWithContext = previousContext
            ? `Previous analysis: ${previousContext}\n\nPlease re-analyze the chart and update your assessment.`
            : undefined;
          result = await analyzeChartImage(rawBase64, 'image/png', promptWithContext, liveTicker, timeframe);
        } else {
          debug.warn('[App] ⚠️ Screenshot failed, using data analysis fallback');
          result = await analyzeMarketData(liveTicker, candles, timeframe);
          result.mode = replayMode === 'active' ? AnalysisMode.REPLAY : AnalysisMode.LIVE;
        }

        result.symbol = liveTicker;
        result.timeframe = timeframe;
      } else {
        throw new Error('Cannot re-analyze: Missing context.');
      }

      result.timestamp = Date.now();
      const newHistory = [result, ...analysisHistory];
      setAnalysisHistory(newHistory);

      // Inject Context into Chat
      const updateMessage: ChatMessage = {
        role: 'user',
        text: `[SYSTEM UPDATE - ${formatTimestamp(Date.now())}]\nI have updated the analysis based on the latest data/image. Here is the new result you should now reference:\n\n${result.technicalAnalysis}\n\nSignal: ${result.signal}\nKey Levels: ${(result.keyLevels || []).join(', ')}`,
        timestamp: Date.now()
      };

      const newChatHistory = [...chatMessages, updateMessage];
      setChatMessages(newChatHistory);

      // Update Session Record
      if (currentSessionId) {
        const session = savedSessions.find(s => s.id === currentSessionId);
        if (session) {
          const updatedSession: SavedSession = {
            ...session,
            analysis: result, // Update latest
            analysisHistory: newHistory, // Save full history
            chatHistory: newChatHistory,
            // If live, update candles
            ...(mode === AppMode.LIVE ? { candles: candles } : {})
          };

          const updatedList = await saveSession(updatedSession);
          setSavedSessions(updatedList);
        }
      }

      setReAnalyzeCompletedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Re-analysis failed.');
      setIsFetchingData(false);
    } finally {
      setIsLoading(false);
    }
  };

  async function handleFetchLiveDataImpl(e?: React.FormEvent, tickerOverride?: string, timeframeOverride?: Timeframe, forceNew: boolean = false) {
    debug.log('[App] 🚀 handleFetchLiveData called:', { tickerOverride, timeframeOverride, forceNew });
    
    if (e) e.preventDefault();
    
    const targetTicker = (tickerOverride || liveTicker).toUpperCase().trim();
    const targetTimeframe = timeframeOverride || timeframe;
    
    debug.log('[App] Target:', { targetTicker, targetTimeframe });
    
    if (!targetTicker) {
      debug.warn('[App] No ticker provided, returning early');
      return;
    }

    // Update state if override provided
    if (tickerOverride) setLiveTicker(targetTicker);
    if (timeframeOverride) setTimeframe(timeframeOverride);

    // Always fetch fresh data for live trading - no caching
    debug.log('[App] Fetching fresh data (caching disabled for live trading)...');

    setIsFetchingData(true);
    setIsInitialLoading(true);
    setAnalysisHistory([]);
    setAnalysisId('');
    setError(null);
    setHasMoreHistory(true);
    setChatMessages([]);
    setCurrentSessionId(null);
    // Clear existing candles to prevent merging with previous symbol data
    setCandles([]);
    
    // If in multi-layout mode, update all panels with the new symbol/timeframe from home screen
    // Preserve the layout structure but refresh all panel data
    if (layoutType !== '1x1') {
      // Clear panel data to force fresh fetch
      setPanelDataMap(new Map());
      // Update all panels with the new symbol and timeframe (batch update)
      updateAllPanels({ symbol: targetTicker, timeframe: targetTimeframe });
    }

    try {
      debug.log('[App] Fetching candle data (including gap filling)...');
      const { data, source } = await fetchCandleData(targetTicker, targetTimeframe);
      if (!data || data.length === 0) {
          throw new Error(`No data found for ${targetTicker}. Please check the ticker.`);
      }
      
      // Additional deduplication check for React key uniqueness
      const seen = new Set<number>();
      const uniqueData = data.filter(candle => {
          if (seen.has(candle.time)) {
              console.warn(`[App] Removing duplicate candle at ${new Date(candle.time).toISOString()}`);
              return false;
          }
          seen.add(candle.time);
          return true;
      });
      
      if (uniqueData.length !== data.length) {
          console.warn(`[App] Removed ${data.length - uniqueData.length} duplicate candles from initial fetch`);
      }
      
      // Check if we need to fetch today's intraday data to fill the gap
      const lastCandle = uniqueData[uniqueData.length - 1];
      const now = Date.now();
      const lastCandleDate = new Date(lastCandle.time);
      const today = new Date();
      
      // If last candle is from a previous day and market might be open, try to get today's data
      const isIntraday = ['1m', '5m', '15m', '30m', '1h', '60m'].includes(targetTimeframe);
      const isDifferentDay = lastCandleDate.toDateString() !== today.toDateString();
      
      debug.log(`[App] Gap fill check: isIntraday=${isIntraday}, isDifferentDay=${isDifferentDay}, source=${source}`);
      
      if (isIntraday && isDifferentDay && source === 'schwab') {
          debug.log(`[App] Last candle is from ${lastCandleDate.toLocaleString()}, fetching today's intraday data...`);
          
          try {
              // Fetch with no endTime to get the most recent data including today
              const { data: freshData } = await fetchCandleData(targetTicker, targetTimeframe);
              
              // Merge and deduplicate
              const mergedMap = new Map<number, CandleData>();
              uniqueData.forEach(c => mergedMap.set(c.time, c));
              freshData.forEach(c => mergedMap.set(c.time, c));
              
              const mergedData = Array.from(mergedMap.values()).sort((a, b) => a.time - b.time);
              
              if (mergedData.length > uniqueData.length) {
                  debug.log(`[App] ✅ Added ${mergedData.length - uniqueData.length} candles from today`);
                  setCandles(mergedData);
              } else {
                  debug.log(`[App] No additional candles to add`);
                  setCandles(uniqueData);
              }
          } catch (e) {
              console.warn('[App] Failed to fetch today\'s data, using historical only:', e);
              setCandles(uniqueData);
          }
      } else {
          debug.log(`[App] Using data as-is (${uniqueData.length} candles)`);
          setCandles(uniqueData);
      }
      
      setDataSource(source);
      setIsFetchingData(false);
      
      debug.log(`[App] ✅ Data loaded with gap filling complete - Total candles: ${uniqueData.length}`);
      
      // Small delay to ensure chart renders with complete data
      setTimeout(() => {
        debug.log('[App] Hiding loading spinner');
        setIsInitialLoading(false);
      }, 100);

      // Chart data loaded successfully - user can now manually trigger analysis
      debug.log('[App] ✅ Chart data ready. User can now perform AI analysis.');

    } catch (err) {
      debug.error('[App] ❌ Error in handleFetchLiveData:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch live data.');
      setIsFetchingData(false);
      setIsInitialLoading(false); // Hide loading spinner on error
    } finally {
      setIsLoading(false);
    }
  };

  // New function: Analyze current chart with AI
  const handleAnalyzeCurrentChart = async () => {
    if (!liveTicker || candles.length === 0) {
      setError('Please load chart data first');
      return;
    }

    setIsLoading(true);
    setError(null);
    // Expand bottom panel and switch to Analysis tab
    setIsBottomPanelExpanded(true);
    setBottomPanelHeight(300);
    setActiveTab('analysis');

    try {
      debug.log('[App] 📸 Capturing chart screenshot for AI analysis...');
      let chartImageData: string | null = null;
      
      // Wait a moment for chart to fully render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Capture entire chart container using html2canvas (includes all labels and UI)
      const chartContainers = Array.from(document.querySelectorAll('[data-testid="live-chart-container"]')) as HTMLElement[];
      let chartContainer = chartContainers
        .map(el => ({ el, rect: el.getBoundingClientRect() }))
        .filter(x => x.rect.width > 0 && x.rect.height > 0)
        .sort((a, b) => (b.rect.width * b.rect.height) - (a.rect.width * a.rect.height))
        [0]?.el;

      if (!chartContainer) {
        chartContainer = chartContainers[0];
      }
      
      console.log('[App] Chart container found:', !!chartContainer);
      console.log('[App][Analyze][Capture] containers:', {
        count: chartContainers.length,
        visibleCount: chartContainers.filter(el => {
          const r = el.getBoundingClientRect();
          return r.width > 0 && r.height > 0;
        }).length
      });
      
      if (chartContainer) {
        // Go up 2 levels to get the full chart panel with header
        if (chartContainer.parentElement?.parentElement) {
          chartContainer = chartContainer.parentElement.parentElement;
          debug.log('[App] Using grandparent container (should include header)');
        } else if (chartContainer.parentElement) {
          chartContainer = chartContainer.parentElement;
          debug.log('[App] Using parent container');
        }
      }
      
      if (chartContainer) {
        // Dynamically import html2canvas
        const html2canvas = (await import('html2canvas')).default;
        
        // Get actual dimensions of the container
        const rect = chartContainer.getBoundingClientRect();
        console.log('[App] Chart container dimensions:', rect.width, 'x', rect.height);

        console.log('[App][Analyze][Capture] env:', {
          ua: navigator.userAgent,
          dpr: window.devicePixelRatio,
          viewport: { w: window.innerWidth, h: window.innerHeight },
          scroll: { x: window.scrollX, y: window.scrollY }
        });
        console.log('[App][Analyze][Capture] element:', {
          tag: chartContainer.tagName,
          id: chartContainer.id,
          className: chartContainer.className,
          rect: {
            x: rect.x,
            y: rect.y,
            w: rect.width,
            h: rect.height,
            top: rect.top,
            left: rect.left
          },
          offset: { w: chartContainer.offsetWidth, h: chartContainer.offsetHeight },
          client: { w: chartContainer.clientWidth, h: chartContainer.clientHeight },
          scroll: { w: chartContainer.scrollWidth, h: chartContainer.scrollHeight }
        });
        try {
          const cs = window.getComputedStyle(chartContainer);
          console.log('[App][Analyze][Capture] computedStyle:', {
            display: cs.display,
            visibility: cs.visibility,
            opacity: cs.opacity,
            position: cs.position,
            overflow: `${cs.overflow}/${cs.overflowX}/${cs.overflowY}`,
            transform: cs.transform,
            filter: cs.filter
          });
        } catch (e) {
          console.warn('[App][Analyze][Capture] failed to read computedStyle', e);
        }
        
        // Ensure container has valid dimensions
        if (rect.width > 0 && rect.height > 0) {
          const canvas = await html2canvas(chartContainer, {
            backgroundColor: '#1e1e1e',
            scale: 1,
            logging: false,
            useCORS: true,
            allowTaint: true,
            width: rect.width,
            height: rect.height,
            x: 0,
            y: 0
          });
          
          chartImageData = canvas.toDataURL('image/png');

          console.log('[App][Analyze][Capture] canvas:', {
            width: canvas.width,
            height: canvas.height,
            dataUrlLength: chartImageData?.length || 0
          });

          try {
            const ctx = canvas.getContext('2d');
            if (ctx && canvas.width > 0 && canvas.height > 0) {
              const points = [
                { name: 'tl', x: 0, y: 0 },
                { name: 'c', x: Math.floor(canvas.width / 2), y: Math.floor(canvas.height / 2) },
                { name: 'br', x: canvas.width - 1, y: canvas.height - 1 },
              ];
              const samples = points.map(p => {
                const d = ctx.getImageData(p.x, p.y, 1, 1).data;
                return { ...p, rgba: Array.from(d) };
              });
              console.log('[App][Analyze][Capture] pixelSamples:', samples);
            }
          } catch (e) {
            console.warn('[App][Analyze][Capture] pixel sample failed', e);
          }

          const rawBase64Candidate = stripBase64Prefix(chartImageData);

          console.log('[App][Analyze][Capture] base64:', {
            rawLength: rawBase64Candidate?.length || 0,
            head: rawBase64Candidate ? rawBase64Candidate.slice(0, 24) : null,
            tail: rawBase64Candidate ? rawBase64Candidate.slice(-24) : null,
          });
          
          // Validate the screenshot is not empty
          if (rawBase64Candidate && rawBase64Candidate.length > 100 && !chartImageData.endsWith('data:,')) {
            debug.log('[App] ✅ Full chart screenshot captured (includes all UI elements)');
            // Store screenshot for Guru chat
            setLatestChartScreenshot(chartImageData);
          } else {
            console.error('[App] ❌ Screenshot capture returned empty or invalid data');
            chartImageData = null;
          }
        } else {
          console.error('[App] ❌ Chart container has zero dimensions:', rect);
        }
      } else {
        debug.warn('[App] ⚠️ Chart container not found, falling back to data analysis');
      }
      
      // Perform AI analysis
      let result: AnalysisResult;
      
      if (chartImageData) {
        // Use image analysis (preferred method)
        console.log(`[App] Using image analysis for ${liveTicker} ${timeframe}`);
        const rawBase64 = stripBase64Prefix(chartImageData);
        result = await analyzeChartImage(rawBase64, 'image/png', undefined, liveTicker, timeframe);
      } else if (layoutType !== '1x1' && panels.length > 1) {
        // Fallback: Multi-chart layout - use multi-timeframe data analysis
        console.log(`[App] Multi-chart layout detected (${layoutType}). Performing multi-timeframe analysis...`);
        
        const candleDataMap = new Map<string, CandleData[]>();
        candleDataMap.set(timeframe, candles);
        
        for (const panel of panels) {
          if (panel.timeframe !== timeframe && panel.symbol === liveTicker) {
            try {
              const { data: tfData } = await fetchCandleData(liveTicker, panel.timeframe as Timeframe);
              candleDataMap.set(panel.timeframe, tfData);
            } catch (error) {
              console.warn(`[App] Failed to fetch ${panel.timeframe} data:`, error);
            }
          }
        }
        
        result = await analyzeMultiTimeframe(candleDataMap, liveTicker);
      } else {
        // Fallback: Single chart - use standard data analysis
        console.log(`[App] Single chart layout. Performing standard data analysis...`);
        result = await analyzeMarketData(liveTicker, candles, timeframe);
      }

      result.symbol = liveTicker;
      result.timeframe = timeframe;
      
      result.timestamp = Date.now();
      const newHistory = [result];
      
      // --- AUTO SAVE START ---
      const newId = Date.now().toString();
      const newSession: SavedSession = {
          id: newId,
          timestamp: Date.now(),
          mode: AppMode.LIVE,
          title: `${liveTicker} ${timeframe} (${formatTimestamp(Date.now())})`,
          analysis: result,
          analysisHistory: newHistory,
          chatHistory: [],
          ticker: liveTicker,
          timeframe: timeframe,
          candles: candles
      };
      
      const updated = await saveSession(newSession);
      setSavedSessions(updated);
      // --- AUTO SAVE END ---

      setAnalysisHistory(newHistory);
      setAnalysisId(newId);
      setCurrentSessionId(newId);

      debug.log('[App] ✅ AI analysis complete');
    } catch (err) {
      debug.error('[App] ❌ Error in handleAnalyzeCurrentChart:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze chart.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeframeChange = (newTimeframe: Timeframe) => {
      if (newTimeframe === timeframe) return;
      handleFetchLiveData(undefined, undefined, newTimeframe);
  };
  
  const handleLoadMoreHistory = async (oldestTime: number) => {
      if (isFetchingHistory || isFetchingData || !hasMoreHistory) return;
      
      setIsFetchingHistory(true);
      try {
          // Fetch older history using the oldest candle time
          console.log('[App] Loading more history before:', new Date(oldestTime).toISOString());
          const { data: historyData } = await fetchCandleData(liveTicker, timeframe, oldestTime - 1);
          
          if (historyData.length > 0) {
              setCandles(prev => {
                  if (!prev) return historyData;
                  const candleMap = new Map<number, CandleData>();
                  historyData.forEach(c => candleMap.set(c.time, c));
                  prev.forEach(c => candleMap.set(c.time, c));
                  const merged = Array.from(candleMap.values()).sort((a, b) => a.time - b.time);
                  
                  if (merged.length === prev.length) {
                      setHasMoreHistory(false);
                  }
                  return merged;
              });
          } else {
              setHasMoreHistory(false);
          }
      } catch (e) {
          console.warn("Failed to load history:", e);
      } finally {
          setIsFetchingHistory(false);
      }
  };

  const handleLoadSession = async (session: SavedSession) => {
      handleReset();
      setMode(session.mode);
      
      // Load history if available, otherwise fallback to single analysis
      const loadedHistory = session.analysisHistory && session.analysisHistory.length > 0 
        ? session.analysisHistory 
        : [session.analysis];
        
      setAnalysisHistory(loadedHistory);
      setChatMessages(session.chatHistory || []);
      setCurrentSessionId(session.id);
      setAnalysisId(session.id);

      if (session.mode === AppMode.LIVE) {
          if (session.ticker) setLiveTicker(session.ticker);
          if (session.timeframe) setTimeframe(session.timeframe);
          
          // Load cached candles first for immediate view
          if (session.candles) setCandles(session.candles);

          // Resume Live Data
          if (session.ticker && session.timeframe) {
              try {
                  const { data: freshData, source } = await fetchCandleData(session.ticker, session.timeframe);
                  setDataSource(source);
                  setCandles(prev => {
                      // Merge logic
                      const candleMap = new Map<number, CandleData>();
                      if (prev) prev.forEach(c => candleMap.set(c.time, c));
                      freshData.forEach(c => candleMap.set(c.time, c));
                      return Array.from(candleMap.values()).sort((a, b) => a.time - b.time);
                  });
              } catch (e) {
                  console.error("Failed to refresh live data on restore", e);
              }
          }

      } else {
          if (session.imageBase64) setSelectedImage(session.imageBase64);
          if (session.imageMimeType) setImageMimeType(session.imageMimeType);
      }
      setIsHistoryOpen(false);
  };

  const handleDeleteSession = async (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      const updated = await deleteSession(id);
      setSavedSessions(updated);
      if (currentSessionId === id) setCurrentSessionId(null);
  };

  const handleExportSessions = async () => {
      await exportSessions();
  };

  const handleImportSessions = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
          const imported = await importSessions(file);
          setSavedSessions(prev => {
              const combined = [...imported, ...prev];
              const unique = Array.from(new Map(combined.map(item => [item.id, item])).values());
              return unique.sort((a, b) => b.timestamp - a.timestamp);
          });
          alert(`Imported ${imported.length} sessions.`);
      } catch (err) {
          console.error("Import failed", err);
          alert("Failed to import sessions. Please check the file format.");
      }
      e.target.value = ''; 
  };

  // Callback for updating candles from WebSocket
  const handleCandleUpdate = useCallback((candle: CandleData) => {
    // IGNORE live updates if in Replay Mode (use ref for immediate access)
    if (replayModeRef.current === 'active' || replayModeRef.current === 'selecting') {
      debug.stream('[App] 🚫 Ignoring live update - replay mode active');
      return;
    }

    setCandles(prev => {
      if (!prev || prev.length === 0) {
        debug.stream('[App] Adding first candle:', new Date(candle.time).toISOString());
        return [candle];
      }
      
      // Find if this candle updates an existing one (same time) or is new
      const lastIndex = prev.length - 1;
      const last = prev[lastIndex];
      
      debug.stream('[App] Candle comparison:', {
        incoming: { time: candle.time, date: new Date(candle.time).toISOString(), close: candle.close },
        last: { time: last.time, date: new Date(last.time).toISOString(), close: last.close },
        action: candle.time === last.time ? 'UPDATE' : candle.time > last.time ? 'ADD NEW' : 'IGNORE (OLD)'
      });
      
      if (candle.time === last.time) {
        // Update existing candle
        debug.stream('[App] Updating candle:', {
          time: new Date(candle.time).toISOString(),
          oldVolume: last.volume,
          newVolume: candle.volume,
          volumeDiff: candle.volume - last.volume
        });
        const updated = [...prev];
        updated[lastIndex] = candle;
        return updated;
      } else if (candle.time > last.time) {
        // Add new candle
        debug.stream('[App] ✅ Adding new candle to chart');
        return [...prev, candle];
      }
      
      console.warn('[App] ⚠️ Ignoring old candle');
      return prev;
    });
  }, []);

  // WebSocket Integration for Real-Time Updates
  useWebSocketUpdates({
    mode,
    liveTicker,
    timeframe,
    dataSource,
    replayMode: (replayMode === 'off' ? 'inactive' : replayMode) as 'inactive' | 'selecting' | 'active',
    layoutType,
    panels,
    candlesLength: candles.length,
    onCandleUpdate: handleCandleUpdate,
    onChartTickUpdate: handleChartTickUpdate,
    onPanelDataUpdate: setPanelDataMap,
    fetchCandleData,
    BinanceStream,
    StockStream
  });

  const handleReset = () => {
    setSelectedImage(null);
    setAnalysisHistory([]);
    setAnalysisId('');
    setError(null);
    setCandles([]);
    setHasMoreHistory(true);
    setChatMessages([]);
    setCurrentSessionId(null);
  };

  const switchMode = (newMode: AppMode) => {
    setMode(newMode);
    handleReset();
  };

  // Bottom panel resize handlers
  useEffect(() => {
    if (!isDraggingPanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      const windowHeight = window.innerHeight;
      const newHeight = windowHeight - e.clientY;
      // Constrain between 40px (collapsed) and 80% of window height
      const constrainedHeight = Math.max(40, Math.min(newHeight, windowHeight * 0.8));
      setBottomPanelHeight(constrainedHeight);
      // Auto-expand if dragging up
      if (constrainedHeight > 50) {
        setIsBottomPanelExpanded(true);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingPanel(false);
      // Snap to collapsed if very small
      if (bottomPanelHeight < 80) {
        setBottomPanelHeight(40);
        setIsBottomPanelExpanded(false);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPanel, bottomPanelHeight]);

  const [preMarketColorHex, setPreMarketColorHex] = useState<string>('#38bdf8');
  const [regularSessionColorHex, setRegularSessionColorHex] = useState<string>('#22c55e');
  const [afterHoursColorHex, setAfterHoursColorHex] = useState<string>('#f97316');

  const hexToRgba = (hex: string, alpha: number): string => {
    let normalized = hex.trim();
    if (normalized.startsWith('#')) normalized = normalized.slice(1);
    if (normalized.length === 3) {
      const r = normalized[0];
      const g = normalized[1];
      const b = normalized[2];
      normalized = r + r + g + g + b + b;
    }
    if (normalized.length !== 6) {
      return `rgba(148, 163, 184, ${alpha})`;
    }
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      return `rgba(148, 163, 184, ${alpha})`;
    }
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div className="h-screen bg-slate-950 text-slate-100 font-sans selection:bg-emerald-500/30 selection:text-emerald-200 overflow-hidden flex flex-col">
      
      {/* Influencer Banner - shows when user came from influencer landing page */}
      <InfluencerBanner />
      
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50 shrink-0">
        <div className="w-full px-3 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => {
            // Clear replay session from localStorage and reload immediately
            // No need to clear React state - page reload handles that
            // This avoids momentary UI flash where user could take actions
            clearReplaySession();
            window.location.reload();
          }}>
            <div className="w-7 h-7 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span className="text-lg font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
              TradeRewind
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
             {/* Cloud sync removed: localStorage-only */}

             <div className="hidden md:flex bg-slate-900 p-0.5 rounded-lg border border-slate-800">
                <button 
                    onClick={() => replayMode === 'off' && switchMode(AppMode.UPLOAD)}
                    disabled={replayMode !== 'off'}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${replayMode !== 'off' ? 'text-slate-600 cursor-not-allowed opacity-50' : mode === AppMode.UPLOAD ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                    title={replayMode !== 'off' ? 'Exit replay mode first' : 'Chart Analysis'}
                >
                    Chart Analysis
                </button>
                <button 
                    onClick={() => replayMode === 'off' && switchMode(AppMode.LIVE)}
                    disabled={replayMode !== 'off'}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${replayMode !== 'off' ? 'text-slate-600 cursor-not-allowed opacity-50' : mode === AppMode.LIVE ? 'bg-emerald-900/30 text-emerald-400 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                    title={replayMode !== 'off' ? 'Exit replay mode first' : 'Live Market'}
                >
                    Live Market
                </button>
                
                {/* Replay Button */}
                {mode === AppMode.LIVE && candles.length > 0 && (
                    <>
                        <div className="w-px h-4 bg-slate-800 mx-1"></div>
                        <button
                            onClick={handleReplayModeClick}
                            className={`px-2 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                                replayMode !== 'off' 
                                ? 'bg-amber-900/30 text-amber-400 shadow-sm border border-amber-500/20' 
                                : 'text-slate-500 hover:text-slate-300'
                            }`}
                        >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8L4.066 11.2z" />
                            </svg>
                            Replay
                        </button>
                    </>
                )}
             </div>

             <div className="flex md:hidden bg-slate-900 p-0.5 rounded-lg border border-slate-800">
               <button
                 onClick={() => replayMode === 'off' && switchMode(AppMode.UPLOAD)}
                 disabled={replayMode !== 'off'}
                 className={`p-2 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${replayMode !== 'off' ? 'text-slate-600 cursor-not-allowed opacity-50' : mode === AppMode.UPLOAD ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                 title={replayMode !== 'off' ? 'Exit replay mode first' : 'Chart Analysis'}
               >
                 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm5 13l3-4 3 2 4-6" />
                 </svg>
                 <span className="hidden sm:inline">Chart</span>
               </button>
               <button
                 onClick={() => replayMode === 'off' && switchMode(AppMode.LIVE)}
                 disabled={replayMode !== 'off'}
                 className={`p-2 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${replayMode !== 'off' ? 'text-slate-600 cursor-not-allowed opacity-50' : mode === AppMode.LIVE ? 'bg-emerald-900/30 text-emerald-400 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                 title={replayMode !== 'off' ? 'Exit replay mode first' : 'Live Market'}
               >
                 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                 </svg>
                 <span className="hidden sm:inline">Live</span>
               </button>

               {mode === AppMode.LIVE && candles.length > 0 && (
                 <>
                   <div className="w-px h-5 bg-slate-800 mx-1"></div>
                   <button
                     onClick={handleReplayModeClick}
                     className={`p-2 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                       replayMode !== 'off'
                         ? 'bg-amber-900/30 text-amber-400 shadow-sm border border-amber-500/20'
                         : 'text-slate-500 hover:text-slate-300'
                     }`}
                     title="Replay"
                   >
                     <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8L4.066 11.2z" />
                     </svg>
                     <span className="hidden sm:inline">Replay</span>
                   </button>
                 </>
               )}
             </div>

             {/* Multi-Chart Layout Controls */}
             {mode === AppMode.LIVE && candles.length > 0 && (
               <div className="hidden md:block">
                 <LayoutSelector
                   currentLayout={layoutType}
                   onLayoutChange={(newLayout) => {
                     // Clear panel data map to force fresh fetch for new layout
                     setPanelDataMap(new Map());
                     // Pass current symbol and timeframe to preserve them when switching layouts
                     changeLayout(newLayout, true, liveTicker, timeframe);
                   }}
                   syncCrosshair={syncCrosshair}
                   syncTimeframe={syncTimeframe}
                   syncSymbol={syncSymbol}
                   onToggleSync={toggleSync}
                   disabled={replayMode !== 'off'}
                 />
               </div>
             )}

             {/* Sign In Menu - Only show on homepage (no chart loaded) */}
             {candles.length === 0 && !isFetchingData && !isRestoringSession && (
               <div className="relative">
                 <SignInMenu
                   onGetStarted={() => console.log('Get started clicked')}
                   onBilling={openFullUpgradeModal}
                 />
               </div>
             )}
          </div>
        </div>
      </header>

      {/* Replay Controls Overlay */}
      <ReplayControls 
        isActive={replayMode === 'active'}
        isPlaying={replayState.isPlaying}
        speed={replayState.speed}
        onTogglePlay={() => setReplayState(s => ({ ...s, isPlaying: !s.isPlaying }))}
        onSpeedChange={(s) => setReplayState(prev => ({ ...prev, speed: s }))}
        onExit={handleReplayModeClick}
        onStepForward={stepReplayForward}
        currentDate={
            replayMode === 'active' && (replayState.displayTime || replayState.replayTime)
            ? new Date(replayState.displayTime || replayState.replayTime).toLocaleString('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) 
            : candles.length > 0 ? new Date(candles[candles.length-1].time).toLocaleString('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : ''
        }
        isLoadingTicks={isFetchingTicks}
        hasTickData={tickBuffer.length > 0}
        replayMode={replayType}
        onReplayModeChange={(mode) => {
            console.log('[App] Replay mode changed to:', mode);
            // Check subscription access when switching to tick mode
            if (mode === 'tick' && !checkReplayAccess('tick', { ticker: liveTicker, timeframe })) {
                return; // Upgrade prompt will be shown
            }
            setReplayType(mode);
            // If switching to tick mode and no ticks, fetch them (1-day window)
            if (mode === 'tick' && tickBuffer.length === 0 && replayMode === 'active') {
                console.log('[App] Switching to tick mode, fetching initial 1-day window...');
                const replayStartTime = replayState.replayTime || fullHistory[0]?.time;
                if (replayStartTime) {
                    const dayMs = 24 * 60 * 60 * 1000;
                    const toTime = replayStartTime + dayMs;
                    
                    console.log(`[App]   From: ${new Date(replayStartTime).toISOString()}`);
                    console.log(`[App]   To: ${new Date(toTime).toISOString()}`);
                    
                    setIsFetchingTicks(true);
                    fetchTickData(liveTicker, replayStartTime, toTime)
                        .then(ticks => {
                            if (ticks && ticks.length > 0) {
                                const sortedTicks = ticks.sort((a, b) => a.time - b.time);
                                setTickBuffer(sortedTicks);
                                console.log(`[App] ✅ Loaded ${sortedTicks.length} ticks for initial window`);
                            }
                        })
                        .catch(e => console.error('[App] Failed to fetch ticks:', e))
                        .finally(() => setIsFetchingTicks(false));
                }
            }
        }}
      />

      {/* Virtual Trading Panel */}
          {replayMode === 'active' && isPaperTradingMinimized && (
            <button
              onClick={() => setIsPaperTradingMinimized(false)}
              className="fixed bottom-4 right-6 z-[190] group"
              aria-label="Open paper trading"
            >
              <div className="relative flex items-center gap-2 bg-gradient-to-r from-orange-600 to-amber-600 hover:brightness-110 text-white px-3 py-2 rounded-full shadow-2xl transition-all duration-300 group-hover:scale-105 border border-white/15">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v18m14-8l-4 4-3-6-3 4-2-3" />
                </svg>
                <span className="font-semibold text-xs hidden sm:inline">Paper Trading</span>
              </div>
            </button>
          )}

          <VirtualTradingPanel 
        isActive={replayMode === 'active' && !isPaperTradingMinimized}
            currentPrice={currentPrice}
            onBuy={handleBuy}
            onSell={handleSell}
            onMinimize={() => setIsPaperTradingMinimized(true)}
            onClosePosition={handleClosePosition}
        trades={virtualTrades}
        balance={virtualBalance}
        equity={virtualEquity}
        timezone={timezone}
          />

      {/* Sidebar / Drawer for History */}
      <div className={`fixed inset-0 z-[100] transition-opacity duration-300 ${isHistoryOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsHistoryOpen(false)} ></div>
          
          {/* Drawer */}
          <div className={`absolute right-0 top-0 h-full w-full max-w-md bg-slate-900 border-l border-slate-800 transform transition-transform duration-300 flex flex-col shadow-2xl ${isHistoryOpen ? 'translate-x-0' : 'translate-x-full'}`}>
              <div className="p-5 border-b border-slate-800 flex items-center justify-between shrink-0">
                  <div>
                    <h2 className="text-xl font-bold text-white">Saved Analysis</h2>
                    <span className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                         <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span>
                         Local Storage
                    </span>
                  </div>
                  <button onClick={() => setIsHistoryOpen(false)} className="text-slate-500 hover:text-white p-1">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                  </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-thin scrollbar-thumb-slate-700">
                  {savedSessions.length === 0 ? (
                      <div className="text-center py-10 text-slate-500">
                          <p>No saved sessions found.</p>
                          <p className="text-sm mt-2">
                             Analyze a chart to auto-save.
                          </p>
                      </div>
                  ) : (
                      savedSessions.map((session, idx) => (
                          <div 
                            key={`${session.id}-${idx}`} 
                            onClick={() => handleLoadSession(session)}
                            className="group relative bg-slate-800 border border-slate-700 rounded-xl p-4 cursor-pointer hover:border-emerald-500/50 hover:bg-slate-800/80 transition-all"
                          >
                              <div className="flex justify-between items-start">
                                  <div>
                                      <div className="flex items-center gap-2 mb-1">
                                          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${session.mode === AppMode.LIVE ? 'bg-emerald-900 text-emerald-400' : 'bg-blue-900 text-blue-400'}`}>
                                              {session.mode}
                                          </span>
                                          <span className="text-xs text-slate-500">{formatTimestamp(session.timestamp)}</span>
                                      </div>
                                      <h3 className="font-semibold text-slate-200">{session.title}</h3>
                                      <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                                          Signal: <span className={session.analysis.signal === 'BUY' ? 'text-emerald-400' : session.analysis.signal === 'SELL' ? 'text-rose-400' : 'text-blue-400'}>{session.analysis.signal}</span> • Risk: {session.analysis.riskLevel}
                                      </p>
                                  </div>
                                  <button 
                                    onClick={(e) => handleDeleteSession(e, session.id)}
                                    className="text-slate-600 hover:text-rose-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Delete"
                                  >
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-7 7-7-7" />
                                      </svg>
                                  </button>
                              </div>
                          </div>
                      ))
                  )}
              </div>

              {/* Data Management Footer */}
              <div className="p-4 border-t border-slate-800 bg-slate-950">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Data Management</h4>
                <div className="grid grid-cols-2 gap-3">
                    <button 
                        onClick={handleExportSessions}
                        className="flex items-center justify-center space-x-2 bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2 rounded-lg text-sm transition-colors border border-slate-700"
                        title="Download backup file"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        <span>Backup JSON</span>
                    </button>
                    
                    <label className="flex items-center justify-center space-x-2 bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2 rounded-lg text-sm transition-colors border border-slate-700 cursor-pointer">
                        <input 
                            type="file" 
                            accept=".json" 
                            onChange={handleImportSessions} 
                            className="hidden"
                        />
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span>Import JSON</span>
                    </label>
                </div>
              </div>
          </div>
      </div>

      <main className="flex-1 overflow-hidden flex flex-col">
        {/* Desktop: Fullscreen Chart + Bottom Panel | Mobile: Scrollable */}
        <div className="hidden md:flex flex-col flex-1 overflow-hidden">
          {/* Chart Area - Fullscreen */}
          <div className="flex-1 overflow-hidden bg-slate-950 flex flex-col">
            {mode === AppMode.LIVE && (candles.length > 0 || isRestoringSession || isInitialLoading || isFetchingData || replayMode === 'selecting' || isPendingReplayRedirect) ? (
              <div className="relative flex-1 h-full flex flex-col group">
                {/* Render Multi-Chart Grid or Single Chart */}
                {layoutType === '1x1' ? (
                  <>
                    {/* Single Chart Mode */}
                    {isInitialLoading || isRestoringSession || (isPendingReplayRedirect && candles.length === 0 && !isFetchingData) ? (
                      <LoadingSpinner message={isPendingReplayRedirect ? "Preparing replay..." : isRestoringSession ? "Restoring session..." : "Loading chart data..."} />
                    ) : (
                      <>
                        <LiveChart 
                          data={candles} 
                          ticker={liveTicker} 
                          timeframe={timeframe} 
                          onLoadMore={handleLoadMoreHistory}
                          isFetchingMore={isFetchingHistory}
                          hasMoreHistory={hasMoreHistory}
                          onTickerChange={(newTicker) => handleFetchLiveData(undefined, newTicker)}
                          onTimeframeChange={handleTimeframeChange}
                          isLoading={isFetchingData}
                          replayMode={replayMode}
                          onChartClick={handleReplayStart}
                          timezone={timezone}
                          onTimezoneChange={setTimezone}
                          preMarketTintColor={hexToRgba(preMarketColorHex, 0.16)}
                          regularSessionTintColor={hexToRgba(regularSessionColorHex, 0.22)}
                          afterHoursTintColor={hexToRgba(afterHoursColorHex, 0.16)}
                        />

                        <ReplayStartSelector
                          isActive={replayMode === 'selecting'}
                          onCancel={cancelReplaySelectingMode}
                          onDateTimeSelect={handleReplayStart}
                          minDate={candles.length > 0 ? (() => {
                            const firstTime = candles[0].time;
                            return firstTime && !isNaN(firstTime) ? new Date(firstTime) : undefined;
                          })() : undefined}
                          maxDate={candles.length > 0 ? (() => {
                            const lastTime = candles[candles.length - 1].time;
                            return lastTime && !isNaN(lastTime) ? new Date(lastTime) : undefined;
                          })() : undefined}
                          timezone={timezone}
                        />

                        {/* Floating AI Analysis Button moved to FloatingGuruChat - unified smart button */}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {/* Multi-Chart Grid Mode */}
                    <MultiChartGrid
                      layoutType={layoutType}
                      panels={panels}
                      replayMode={replayMode}
                      panelData={panelDataMap}
                      panelHistoryState={panelHistoryState}
                      onSymbolChange={handlePanelSymbolChange}
                      onTimeframeChange={handlePanelTimeframeChange}
                      onPanelUpdate={updatePanel}
                      onLoadMoreHistory={handleLoadMorePanelHistory}
                    />
                    
                        <ReplayStartSelector
                          isActive={replayMode === 'selecting'}
                          onCancel={cancelReplaySelectingMode}
                          onDateTimeSelect={handleReplayStart}
                          minDate={(() => {
                            const firstPanelData = panels.length > 0 ? panelDataMap.get(panels[0].id) : undefined;
                            if (firstPanelData && firstPanelData.length > 0) {
                              const firstTime = firstPanelData[0].time;
                              if (firstTime && !isNaN(firstTime)) {
                                return new Date(firstTime);
                              }
                            }
                            if (candles.length > 0) {
                              const firstTime = candles[0].time;
                              if (firstTime && !isNaN(firstTime)) {
                                return new Date(firstTime);
                              }
                            }
                            return undefined;
                          })()}
                          maxDate={(() => {
                            const firstPanelData = panels.length > 0 ? panelDataMap.get(panels[0].id) : undefined;
                            if (firstPanelData && firstPanelData.length > 0) {
                              const lastTime = firstPanelData[firstPanelData.length - 1].time;
                              if (lastTime && !isNaN(lastTime)) {
                                return new Date(lastTime);
                              }
                            }
                            if (candles.length > 0) {
                              const lastTime = candles[candles.length - 1].time;
                              if (lastTime && !isNaN(lastTime)) {
                                return new Date(lastTime);
                              }
                            }
                            return undefined;
                          })()}
                          timezone={timezone}
                        />
                  </>
                )}
              </div>
            ) : mode === AppMode.UPLOAD && selectedImage ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="max-w-5xl w-full h-full bg-slate-900 rounded-2xl border border-slate-800 p-2 shadow-2xl shadow-black/50 relative overflow-hidden group flex items-center justify-center">
                  <img 
                    src={selectedImage} 
                    alt="Chart Analysis" 
                    className="max-w-full max-h-full object-contain rounded-xl"
                  />
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button 
                      onClick={handleReset}
                      className="bg-slate-900/90 hover:bg-slate-800 text-white p-2 rounded-full backdrop-blur-sm border border-slate-700 shadow-lg"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center space-y-4">
                  {mode === AppMode.LIVE ? (
                    <>
                      <svg className="w-16 h-16 mx-auto text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                      </svg>
                      <div>
                        <input 
                          type="text" 
                          value={tickerInput}
                          onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                          onFocus={(e) => e.currentTarget.select()}
                          placeholder="Enter Ticker (e.g. BTCUSDT, AAPL)"
                          className="bg-slate-900 border border-slate-700 rounded-xl px-6 py-3 text-white placeholder-slate-600 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-center"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const nextTicker = tickerInput.toUpperCase().trim();
                              setLiveTicker(nextTicker);
                              handleFetchLiveData(undefined, nextTicker);
                            }
                          }}
                        />
                        <div className="flex items-center justify-center gap-2 mt-4">
                          {(['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w'] as Timeframe[]).map((tf) => (
                            <button
                              key={tf}
                              onClick={() => setTimeframe(tf)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                                ${timeframe === tf ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'}`}
                            >
                              {tf.toUpperCase()}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={() => handleFetchLiveData(undefined, tickerInput)}
                          disabled={!tickerInput.trim() || isFetchingData}
                          className="mt-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-8 py-3 rounded-xl font-medium transition-colors shadow-lg shadow-emerald-500/20"
                        >
                          {isFetchingData ? 'Loading...' : 'Load Chart'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <UploadZone 
                      onImageSelected={handleImageSelected} 
                      onError={setError} 
                      isAnalyzing={isLoading} 
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bottom Analysis Panel - TradingView Style */}
          {(analysis || selectedImage || candles.length > 0) && (
            <div 
              className="bg-slate-900 border-t border-slate-800 flex flex-col relative"
              style={{ height: isBottomPanelExpanded ? `${bottomPanelHeight}px` : '40px' }}
            >
              {/* Drag Handle */}
              <div 
                className="absolute top-0 left-0 right-0 h-1 cursor-ns-resize hover:bg-emerald-500/50 transition-colors z-10"
                onMouseDown={() => setIsDraggingPanel(true)}
                title="Drag to resize"
              />
              
              {/* Tab Bar */}
              <div className="flex items-center justify-between border-b border-slate-800 px-3 h-10 shrink-0">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setActiveTab('analysis')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors relative ${ 
                      activeTab === 'analysis' ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    Analysis
                    {activeTab === 'analysis' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500"></div>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('replay')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors relative ${ 
                    activeTab === 'replay' ? 'text-amber-400' : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    Replay Trading
                    {activeTab === 'replay' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500"></div>
                    )}
                  </button>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Timezone Selector */}
                  <div className="relative flex items-center">
                    <svg className="w-3 h-3 text-slate-500 absolute left-2 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <select 
                      value={timezone} 
                      onChange={(e) => setTimezone(e.target.value)}
                      className="bg-slate-800 text-xs text-slate-400 hover:text-slate-200 pl-6 pr-2 py-1.5 rounded-lg border border-slate-700 hover:border-slate-600 outline-none appearance-none cursor-pointer transition-colors"
                      title="Change Timezone"
                    >
                      <option value="America/New_York">New York (UTC-5)</option>
                      <option value="Europe/London">London (UTC+0)</option>
                      <option value="Asia/Tokyo">Tokyo (UTC+9)</option>
                      <option value="Asia/Hong_Kong">Hong Kong (UTC+8)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                  <button
                    onClick={() => setIsChartSettingsOpen(true)}
                    className="ml-2 p-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-emerald-400 hover:border-emerald-500 bg-slate-900/60"
                    title="Open chart settings"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.894 3.31.873 2.416 2.416a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.894 1.543-.873 3.31-2.416 2.416a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.894-3.31-.873-2.416-2.416a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.894-1.543.873-3.31 2.416-2.416.996.577 2.251.074 2.573-1.066z"
                      />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      if (isBottomPanelExpanded) {
                        setBottomPanelHeight(40);
                        setIsBottomPanelExpanded(false);
                      } else {
                        setBottomPanelHeight(300);
                        setIsBottomPanelExpanded(true);
                      }
                    }}
                    className="p-1.5 hover:bg-slate-800 rounded transition-colors text-slate-400 hover:text-white"
                    title={isBottomPanelExpanded ? "Collapse panel" : "Expand panel"}
                  >
                    <svg className={`w-4 h-4 transition-transform ${isBottomPanelExpanded ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              {isBottomPanelExpanded && (
                <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 px-4 py-4">
                  {/* Analysis Loading State - Only show full loader for first analysis (no existing analysis) */}
                  {activeTab === 'analysis' && isLoading && !analysis && (
                    <div className="flex flex-col items-center justify-center py-12 space-y-6">
                      {/* Animated Trading Guru Icon */}
                      <div className="relative">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center">
                          <svg className="w-10 h-10 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        {/* Pulsing ring animation */}
                        <div className="absolute inset-0 rounded-full border-2 border-emerald-500/30 animate-ping" />
                        <div className="absolute inset-0 rounded-full border border-emerald-500/50 animate-pulse" />
                      </div>
                      
                      {/* Title */}
                      <div className="text-center space-y-2">
                        <h3 className="text-lg font-semibold text-slate-200">Trading Guru is Analyzing Chart...</h3>
                        <p className="text-sm text-slate-400">Examining chart patterns, indicators, and market context...</p>
                      </div>
                      
                      {/* Modern Progress Bar */}
                      <div className="w-80 space-y-3">
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-emerald-500 via-blue-500 to-emerald-500 rounded-full animate-[shimmer_2s_infinite] bg-[length:200%_100%]" 
                               style={{ animation: 'shimmer 2s infinite linear' }} />
                        </div>
                        
                        {/* Analysis steps indicator */}
                        <div className="flex justify-between text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Reading patterns
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" style={{ animationDelay: '0.3s' }} />
                            Analyzing trends
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" style={{ animationDelay: '0.6s' }} />
                            Generating insights
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'analysis' && analysis ? (
                    <div ref={analysisResultsRef} className="space-y-4 max-w-7xl mx-auto">
                      {/* Re-analyzing indicator - shown when loading with existing analysis */}
                      {isLoading && (
                        <div className="bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-4">
                          <div className="flex items-center gap-4">
                            <div className="relative flex-shrink-0">
                              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                <svg className="w-5 h-5 text-emerald-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div className="absolute inset-0 rounded-full border border-emerald-500/50 animate-ping" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-emerald-400">Re-analyzing chart...</p>
                              <p className="text-xs text-slate-400 mt-0.5">Previous analysis shown below • New insights coming soon</p>
                            </div>
                            <div className="w-32 h-1.5 bg-slate-800 rounded-full overflow-hidden flex-shrink-0">
                              <div className="h-full bg-gradient-to-r from-emerald-500 via-blue-500 to-emerald-500 rounded-full bg-[length:200%_100%]" 
                                   style={{ animation: 'shimmer 2s infinite linear' }} />
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-slate-300 flex items-center gap-2">
                                {isLoading ? 'Previous Analysis' : 'Analysis Results'}
                                <span 
                                    className={`text-xs font-medium px-2 py-1 rounded-full border flex items-center gap-1 transition-colors ${
                                        analysis.mode === 'REPLAY' 
                                        ? 'text-amber-400 bg-amber-500/10 border-amber-500/30 shadow-sm shadow-amber-900/20' 
                                        : 'text-slate-400 bg-slate-800 border-slate-700'
                                    }`}
                                    title={analysis.mode === 'REPLAY' ? `Analysis generated at: ${formatTimestamp(analysis.timestamp || Date.now())}` : "Time of analysis generation"}
                                >
                                    {mode === AppMode.LIVE && (analysis.symbol || liveTicker) && (
                                      <>
                                        {analysis.symbol || liveTicker} • {analysis.timeframe || timeframe} •{' '}
                                      </>
                                    )}
                                    {analysis.mode === 'REPLAY' && (
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )}
                                    {analysis.mode === 'REPLAY' ? 'Replay Time: ' : 'Generated: '}
                                    {formatTimestamp(
                                        (analysis.mode === 'REPLAY' && analysis.candleTimestamp) 
                                        ? analysis.candleTimestamp 
                                        : (analysis.timestamp || Date.now())
                                    )}
                                </span>
                          </h3>
                      </div>

                      {/* Top Indicators Row */}
                      <div className="grid grid-cols-4 gap-4">
                        <SignalGauge signal={analysis.signal} confidence={analysis.confidenceScore} />
                        
                        {/* Signal Card */}
                        <div className={`p-4 rounded-xl border flex flex-col justify-between ${ 
                          analysis.signal === SignalType.BUY ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 
                          analysis.signal === SignalType.SELL ? 'bg-rose-500/10 border-rose-500/30 text-rose-400' :
                          'bg-blue-500/10 border-blue-500/30 text-blue-400'
                        }`}>
                          <span className="text-xs uppercase tracking-widest font-semibold opacity-70">Recommendation</span>
                          <div className="flex items-center space-x-2 mt-2">
                            {analysis.signal === SignalType.BUY && (
                              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                            )}
                            {analysis.signal === SignalType.SELL && (
                              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>
                            )}
                            {analysis.signal === SignalType.NEUTRAL && (
                              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" /></svg>
                            )}
                            <span className="text-2xl font-bold tracking-tight">{analysis.signal}</span>
                          </div>
                        </div>

                        {/* Risk Card */}
                        <div className="bg-slate-800/50 border border-slate-700 p-4 rounded-xl flex flex-col justify-between">
                          <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">Risk Level</span>
                          <div className="mt-2">
                            <span className={`text-2xl font-bold ${ 
                              analysis.riskLevel === 'High' ? 'text-rose-400' :
                              analysis.riskLevel === 'Medium' ? 'text-amber-400' : 'text-emerald-400'
                            }`}>
                              {analysis.riskLevel}
                            </span>
                          </div>
                        </div>
                        
                        {/* Key Levels Summary */}
                        <div className="bg-slate-800/50 border border-slate-700 p-4 rounded-xl">
                          <span className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2 block">Key Levels</span>
                          <div className="flex flex-wrap gap-1.5">
                            {(analysis.keyLevels || []).slice(0, 3).map((level, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded font-mono">
                                {level}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Detailed Analysis Text */}
                      <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden group/analysis">
                        <div className="flex items-center justify-between p-4">
                            <button 
                              onClick={() => setIsAnalysisTextExpanded(!isAnalysisTextExpanded)}
                              className="flex items-center gap-2 text-sm font-semibold text-white hover:text-emerald-400 transition-colors"
                            >
                              <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Technical Breakdown
                              <svg 
                                className={`w-4 h-4 text-slate-400 transition-transform ${isAnalysisTextExpanded ? 'rotate-180' : ''}`} 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-slate-500 font-mono">
                                  {formatTimestamp(analysis.timestamp || Date.now())}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAnalysis(null);
                                }}
                                className="p-1.5 rounded-lg hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 transition-all opacity-0 group-hover/analysis:opacity-100"
                                title="Delete analysis"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                        </div>
                        {isAnalysisTextExpanded && (
                          <div className="px-6 pb-6 border-t border-slate-700/50 pt-4">
                            <MarkdownRenderer content={analysis.technicalAnalysis} />
                          </div>
                        )}
                      </div>

                      {/* Previous Analyses List (Desktop) */}
                      {analysisHistory.length > 1 && (
                        <div className="mt-6 border-t border-slate-800 pt-6">
                            <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-4 flex items-center gap-2">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Previous Analysis History
                            </h4>
                            <div className="space-y-4 pl-4 border-l-2 border-slate-800 ml-1">
                                {analysisHistory.slice(1).map((item, idx) => {
                                    const isExpanded = expandedHistoryItems.has(idx);
                                    return (
                                    <div key={idx} className="relative group/historyitem">
                                        <div className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-slate-700 border-2 border-slate-900 group-hover/historyitem:bg-slate-500 transition-colors"></div>
                                        <div className="bg-slate-800/20 border border-slate-800 rounded-lg overflow-hidden">
                                            {/* Header: Time, Metrics, and Controls - Always Visible */}
                                            <div className="p-4">
                                                {/* Top Row: Timestamp, Signal, Delete */}
                                                <div className="flex items-center justify-between mb-3">
                                                    <button
                                                      onClick={() => {
                                                        const newExpanded = new Set(expandedHistoryItems);
                                                        if (isExpanded) {
                                                          newExpanded.delete(idx);
                                                        } else {
                                                          newExpanded.add(idx);
                                                        }
                                                        setExpandedHistoryItems(newExpanded);
                                                      }}
                                                      className="flex items-center gap-2 hover:text-emerald-400 transition-colors"
                                                    >
                                                      <span className="text-xs font-mono text-slate-500">
                                                          {mode === AppMode.LIVE && (item.symbol || liveTicker) ? `${item.symbol || liveTicker} • ${item.timeframe || timeframe} • ` : ''}
                                                          {item.mode === 'REPLAY' && item.candleTimestamp 
                                                              ? formatTimestamp(item.candleTimestamp) 
                                                              : formatTimestamp(item.timestamp!)}
                                                      </span>
                                                      {item.mode === 'REPLAY' && (
                                                          <span className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded">
                                                              REPLAY
                                                          </span>
                                                      )}
                                                      <svg 
                                                        className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                                                        fill="none" 
                                                        viewBox="0 0 24 24" 
                                                        stroke="currentColor"
                                                      >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                      </svg>
                                                    </button>
                                                    <div className="flex items-center gap-2">
                                                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${ 
                                                          item.signal === SignalType.BUY ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 
                                                          item.signal === SignalType.SELL ? 'border-rose-500/30 text-rose-400 bg-rose-500/5' : 
                                                          'border-blue-500/30 text-blue-400 bg-blue-500/5'
                                                      }`}>
                                                          {item.signal}
                                                      </span>
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          // Remove this item from history
                                                          const newHistory = analysisHistory.filter((_, i) => i !== idx + 1);
                                                          setAnalysisHistory(newHistory);
                                                          // Update expanded items indices
                                                          const newExpanded = new Set<number>();
                                                          expandedHistoryItems.forEach(i => {
                                                            if (i < idx) newExpanded.add(i);
                                                            else if (i > idx) newExpanded.add(i - 1);
                                                          });
                                                          setExpandedHistoryItems(newExpanded);
                                                        }}
                                                        className="p-1 rounded hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 transition-all opacity-0 group-hover/historyitem:opacity-100"
                                                        title="Delete this analysis"
                                                      >
                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                      </button>
                                                    </div>
                                                </div>

                                                {/* Metrics Grid - Always Visible */}
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                                    {/* Risk */}
                                                    <div className="bg-slate-900/40 rounded p-2 flex items-center gap-2">
                                                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Risk:</span>
                                                        <span className={`text-xs font-medium ${ 
                                                            item.riskLevel === 'High' ? 'text-rose-400' :
                                                            item.riskLevel === 'Medium' ? 'text-amber-400' : 'text-emerald-400'
                                                        }`}>
                                                            {item.riskLevel}
                                                        </span>
                                                    </div>
                                                    
                                                    {/* Confidence */}
                                                    <div className="bg-slate-900/40 rounded p-2 flex items-center gap-2">
                                                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Conf:</span>
                                                        <div className="flex items-center gap-1 flex-1">
                                                            <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                                                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${item.confidenceScore}%` }}></div>
                                                            </div>
                                                            <span className="text-[10px] text-slate-300">{item.confidenceScore}%</span>
                                                        </div>
                                                    </div>

                                                    {/* Key Levels */}
                                                    <div className="bg-slate-900/40 rounded p-2">
                                                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Levels:</span>
                                                        <div className="flex flex-wrap gap-1">
                                                            {(item.keyLevels || []).slice(0, 2).map((level, i) => (
                                                                <span key={i} className="text-[10px] font-mono text-slate-300 bg-slate-800 px-1 rounded">
                                                                    {level}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Analysis Text - Only when expanded */}
                                            {isExpanded && (
                                                <div className="px-4 pb-4 pt-0 border-t border-slate-700/50 mt-2 pt-3">
                                                    <MarkdownRenderer content={item.technicalAnalysis} className="text-xs" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                      )}
                    </div>
                  ) : activeTab === 'replay' ? (
                    <div className="h-full w-full">
                        <ReplayTradingPanel 
                            trades={virtualTrades}
                            balance={virtualBalance}
                            initialBalance={100000}
                            equity={virtualEquity}
                            timezone={timezone}
                          />
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-500">
                      <div className="text-center">
                        <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm">Run analysis to see results</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Layout - Original Scrollable Design */}
        <div className="md:hidden flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-7xl mx-auto px-3 pt-3 pb-6">
            
            {/* Mode Switcher Mobile */}
            <div className="hidden md:hidden flex mb-6 bg-slate-900 p-1 rounded-xl border border-slate-800">
                <button 
                    onClick={() => switchMode(AppMode.UPLOAD)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${mode === AppMode.UPLOAD ? 'bg-slate-800 text-white shadow' : 'text-slate-500'}`}
                >
                    Upload Chart
                </button>
                <button 
                    onClick={() => switchMode(AppMode.LIVE)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${mode === AppMode.LIVE ? 'bg-emerald-900/30 text-emerald-400 shadow' : 'text-slate-500'}`}
                >
                    Live Data
                </button>
            </div>

            {/* Main Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* Left Column: Inputs & Visuals */}
                <div className="lg:col-span-5 space-y-6">
                
                {mode === AppMode.UPLOAD ? (
                    <>
                        {/* Image Display or Upload Zone */}
                        {!selectedImage ? (
                            <div className="animate-fade-in">
                                <h2 className="text-2xl font-bold mb-4">Upload Chart</h2>
                                <p className="text-slate-400 mb-6">Upload a screenshot or paste an image for instant analysis.</p>
                                <UploadZone 
                                    onImageSelected={handleImageSelected} 
                                    onError={setError} 
                                    isAnalyzing={isLoading} 
                                />
                            </div>
                        ) : (
                            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-1 shadow-2xl shadow-black/50 relative overflow-hidden group">
                                <img 
                                src={selectedImage} 
                                alt="Chart Analysis" 
                                className="w-full h-auto rounded-xl block"
                                />
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <button 
                                    onClick={handleReset}
                                    className="bg-slate-900/90 hover:bg-slate-800 text-white p-2 rounded-full backdrop-blur-sm border border-slate-700 shadow-lg"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                                </div>
                            </div>
                        )}
                        
                        {/* Analyze Button (Upload Mode) */}
                        {selectedImage && !analysis && (
                            <button
                                onClick={handleAnalyzeImage}
                                disabled={isLoading}
                                className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all duration-200 flex items-center justify-center space-x-2
                                ${isLoading ? 'bg-slate-800 text-slate-400' : 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white'}`}
                            >
                                {isLoading ? <span>Analyzing...</span> : <span>Analyze Chart</span>}
                            </button>
                        )}
                    </>
                ) : (
                    <div className="animate-fade-in space-y-6">
                        {/* Only show ticker input section when no chart is loaded and not restoring session */}
                        {candles.length === 0 && !isFetchingData && !isRestoringSession && (
                          <>
                            <div>
                                <h2 className="text-2xl font-bold mb-2">Live Market Analysis</h2>
                                <p className="text-slate-400 text-sm">Fetch real-time candles and get AI-driven signals.</p>
                            </div>

                            {/* Ticker Input */}
                            <form onSubmit={(e) => {
                              e.preventDefault();
                              const nextTicker = tickerInput.toUpperCase().trim();
                              setLiveTicker(nextTicker);
                              handleFetchLiveData(undefined, nextTicker);
                            }} className="flex gap-2">
                                <input 
                                    type="text" 
                                    value={tickerInput}
                                    onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                                    onFocus={(e) => e.currentTarget.select()}
                                    placeholder="Enter Ticker (e.g. BTCUSDT, AAPL)"
                                    className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                                />
                                <button 
                                    type="submit"
                                    disabled={isFetchingData || isLoading || !tickerInput.trim()}
                                    className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:hover:bg-emerald-600 text-white px-6 rounded-xl font-medium transition-colors shadow-lg shadow-emerald-500/20"
                                >
                                Go
                                </button>
                            </form>

                            {/* Timeframe Selector */}
                            <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-none">
                                {(['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w'] as Timeframe[]).map((tf) => (
                                    <button
                                        key={tf}
                                        type="button"
                                        onClick={() => setTimeframe(tf)}
                                        className={`
                                            px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0
                                            ${timeframe === tf 
                                                ? 'bg-slate-700 text-white shadow-sm border border-slate-600' 
                                                : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'}
                                        `}
                                    >
                                        {tf.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                          </>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div className="relative group p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-sm flex items-start space-x-3">
                                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="flex-1">
                                    <span className="font-semibold block mb-1">Data Fetch Error</span>
                                    <span className="opacity-90">{error}</span>
                                </div>
                                {/* Close button - visible on hover */}
                                <button
                                    onClick={() => setError(null)}
                                    className="absolute top-2 right-2 p-1 rounded-full hover:bg-rose-500/20 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Dismiss"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        )}

                        {/* Live Chart Component */}
                        {isFetchingData || isRestoringSession ? (
                            <div className="h-[400px] flex flex-col items-center justify-center space-y-4 text-emerald-500 bg-slate-900/20 rounded-xl border border-slate-800/50">
                                <div className="relative w-12 h-12">
                                        <div className="absolute top-0 left-0 w-full h-full border-4 border-slate-700 rounded-full"></div>
                                        <div className="absolute top-0 left-0 w-full h-full border-4 border-emerald-500 rounded-full animate-spin border-t-transparent"></div>
                                </div>
                                <span className="text-sm font-medium tracking-wide">{isRestoringSession ? 'Restoring session...' : 'Fetching market data...'}</span>
                            </div>
                        ) : candles.length > 0 ? (
                            <div className="relative h-[400px] flex flex-col">
                                {/* Tick loading indicator is now shown in ReplayControls */}
                                <LiveChart 
                                    data={candles} 
                                    ticker={liveTicker} 
                                    timeframe={timeframe} 
                                    onLoadMore={handleLoadMoreHistory}
                                    isFetchingMore={isFetchingHistory}
                                    hasMoreHistory={hasMoreHistory}
                                    onTickerChange={(newTicker) => handleFetchLiveData(undefined, newTicker)}
                                    onTimeframeChange={handleTimeframeChange}
                                    isLoading={isFetchingData}
                                    replayMode={replayMode}
                                    onChartClick={handleReplayStart}
                                    timezone={timezone}
                                    onTimezoneChange={setTimezone}
                                />
                                {/* Floating Analyze Button moved to FloatingGuruChat - unified smart button */}
                            </div>
                        ) : !error ? (
                            <div className="h-[300px] bg-slate-900/30 border-2 border-dashed border-slate-800 rounded-xl flex flex-col items-center justify-center text-slate-600">
                                <svg className="w-12 h-12 mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                </svg>
                                <span className="text-sm">Enter a ticker to load live chart</span>
                                <span className="text-xs mt-2 px-2 py-1 bg-slate-800 rounded text-slate-500">Try: BTCUSDT, ETHUSDT, AAPL</span>
                            </div>
                        ) : null}

                        {/* Analysis Loading State for Live Mode */}
                        {isLoading && !isFetchingData && (
                            <div className="flex items-center justify-center py-4 space-x-3 text-blue-400 animate-pulse">
                                <span>Analyzing Price Action...</span>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Helper Ticker (Only in Upload Mode) */}
                {mode === AppMode.UPLOAD && <StockTicker />}

                </div>

                {/* Right Column: Analysis Results */}
                <div className="lg:col-span-7 pb-20">
                {analysis ? (
                    <div className="space-y-6 animate-fade-in-up">
                    
                    {/* Results Header with Auto-Save Status and Re-Analyze */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div>
                            <h3 className="text-lg font-semibold text-slate-300 flex items-center gap-2">
                                Analysis Results
                                <span 
                                    className={`text-xs font-medium px-2 py-1 rounded-full border flex items-center gap-1 transition-colors ${
                                        analysis.mode === 'REPLAY' 
                                        ? 'text-amber-400 bg-amber-500/10 border-amber-500/30 shadow-sm shadow-amber-900/20' 
                                        : 'text-slate-400 bg-slate-800 border-slate-700'
                                    }`}
                                    title={analysis.mode === 'REPLAY' ? `Analysis generated at: ${formatTimestamp(analysis.timestamp || Date.now())}` : "Time of analysis generation"}
                              >
                                    {mode === AppMode.LIVE && (analysis.symbol || liveTicker) && (
                                      <>
                                        {analysis.symbol || liveTicker} • {analysis.timeframe || timeframe} •{' '}
                                      </>
                                    )}
                                    {analysis.mode === 'REPLAY' && (
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )}
                                    {analysis.mode === 'REPLAY' ? 'Replay Time: ' : 'Generated: '}
                                    {formatTimestamp(
                                        (analysis.mode === 'REPLAY' && analysis.candleTimestamp) 
                                        ? analysis.candleTimestamp 
                                        : (analysis.timestamp || Date.now())
                                    )}
                                </span>
                            </h3>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 border border-emerald-500/20 rounded-lg">
                                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                                <span className="text-xs font-medium text-emerald-400 uppercase tracking-wide">Auto-Saved</span>
                            </div>
                        </div>
                    </div>

                    {/* Top Indicators Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-1">
                            <SignalGauge signal={analysis.signal} confidence={analysis.confidenceScore} />
                        </div>
                        
                        <div className="md:col-span-2 grid grid-cols-2 gap-4">
                            {/* Signal Card */}
                            <div className={`
                                p-5 rounded-xl border flex flex-col justify-between
                                ${analysis.signal === SignalType.BUY ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 
                                analysis.signal === SignalType.SELL ? 'bg-rose-500/10 border-rose-500/30 text-rose-400' :
                                'bg-blue-500/10 border-blue-500/30 text-blue-400'}
                            `}>
                                <span className="text-xs uppercase tracking-widest font-semibold opacity-70">Recommendation</span>
                                <div className="flex items-center space-x-3 mt-2">
                                    {analysis.signal === SignalType.BUY && (
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                                    )}
                                    {analysis.signal === SignalType.SELL && (
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>
                                    )}
                                    {analysis.signal === SignalType.NEUTRAL && (
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" /></svg>
                                    )}
                                    <span className="text-3xl font-bold tracking-tight">{analysis.signal}</span>
                                </div>
                            </div>

                            {/* Risk Card */}
                            <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl flex flex-col justify-between">
                                <span className="text-xs uppercase tracking-widest font-semibold text-slate-500">Risk Level</span>
                                <div className="mt-2">
                                    <span className={`text-2xl font-bold ${ 
                                        analysis.riskLevel === 'High' ? 'text-rose-400' :
                                        analysis.riskLevel === 'Medium' ? 'text-amber-400' : 'text-emerald-400'
                                    }`}>
                                        {analysis.riskLevel}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Key Levels Summary */}
                            <div className="col-span-2 bg-slate-900 border border-slate-800 p-4 rounded-xl">
                                <span className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2 block">Key Levels</span>
                                <div className="flex flex-wrap gap-2">
                                    {(analysis.keyLevels || []).map((level, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-slate-800 text-slate-300 text-xs rounded font-mono border border-slate-700">
                                            {level}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Analysis Text */}
                    <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 shadow-inner">
                        <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                            <svg className="w-5 h-5 mr-2 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Technical Breakdown
                        </h3>
                        <MarkdownRenderer content={analysis.technicalAnalysis} />
                    </div>
                    
                    {/* Previous Analyses List */}
                    {analysisHistory.length > 1 && (
                        <div className="mt-8 border-t border-slate-800 pt-8">
                            <h4 className="text-sm uppercase tracking-widest font-semibold text-slate-500 mb-4 flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Previous Analysis History
                            </h4>
                            <div className="space-y-6 pl-4 border-l-2 border-slate-800 ml-2">
                                {analysisHistory.slice(1).map((item, idx) => (
                                    <div key={idx} className="relative">
                                        <div className="absolute -left-[25px] top-0 w-3 h-3 rounded-full bg-slate-700 border-2 border-slate-900"></div>
                                        <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-6 hover:bg-slate-900/50 transition-colors">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-xs font-mono text-slate-500">
                                                  {mode === AppMode.LIVE && (item.symbol || liveTicker) ? `${item.symbol || liveTicker} • ${item.timeframe || timeframe} • ` : ''}
                                                  {formatTimestamp(item.timestamp!)}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded border ${ 
                                                        item.signal === SignalType.BUY ? 'border-emerald-500/30 text-emerald-400' : 
                                                        item.signal === SignalType.SELL ? 'border-rose-500/30 text-rose-400' : 
                                                        'border-blue-500/30 text-blue-400'
                                                    }`}>
                                                        {item.signal}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="prose prose-invert prose-sm max-w-none text-slate-400 line-clamp-6 hover:line-clamp-none transition-all">
                                                {item.technicalAnalysis}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Chat Interface - Only show in Upload mode, Live mode uses FloatingGuruChat */}
                    {mode === AppMode.UPLOAD && (
                      <ChatInterface 
                          key={analysisId} 
                          imageBase64={stripBase64Prefix(selectedImage || '')} 
                          imageMimeType={imageMimeType} 
                          initialAnalysis={analysis.technicalAnalysis}
                          restoredMessages={chatMessages}
                          onMessagesChange={setChatMessages}
                      />
                    )}

                    </div>
                ) : (
                    /* Placeholder State */
                    <div className="h-full min-h-[400px] rounded-3xl border-2 border-dashed border-slate-800 bg-slate-900/30 flex flex-col items-center justify-center text-slate-600">
                        {isLoading || isFetchingData ? (
                            <div className="flex flex-col items-center animate-pulse space-y-4">
                                <div className="h-2 w-48 bg-slate-800 rounded"></div>
                                <div className="h-2 w-32 bg-slate-800 rounded"></div>
                                <div className="h-2 w-40 bg-slate-800 rounded"></div>
                            </div>
                        ) : (
                            <>
                                <svg className="w-16 h-16 mb-4 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-sm font-medium">Analysis results will appear here</span>
                            </>
                        )}
                    </div>
                )}
                </div>
            </div>
        </div>
        </div>
      </main>

      <ChartSettingsPanel
        isOpen={isChartSettingsOpen}
        onClose={() => setIsChartSettingsOpen(false)}
        sessionColors={{
          preMarket: preMarketColorHex,
          regular: regularSessionColorHex,
          afterHours: afterHoursColorHex,
        }}
        onSessionColorsChange={(colors) => {
          setPreMarketColorHex(colors.preMarket);
          setRegularSessionColorHex(colors.regular);
          setAfterHoursColorHex(colors.afterHours);
        }}
      />

      {/* Floating Guru Button - Smart unified button for analyze/chat */}
      {mode === AppMode.LIVE && (
        <FloatingGuruChat
          imageBase64={stripBase64Prefix(latestChartScreenshot)}
          imageMimeType={imageMimeType}
          initialAnalysis={analysis?.technicalAnalysis || ''}
          restoredMessages={chatMessages}
          onMessagesChange={setChatMessages}
          hasAnalysis={!!analysis}
          onAnalyze={handleGuruAnalyze}
          onReAnalyze={handleGuruReAnalyze}
          isAnalyzing={isLoading}
          canAnalyze={candles.length > 0}
          raiseButtonOnMobile={replayMode === 'active' && isPaperTradingMinimized}
          reAnalyzeCompletedAt={reAnalyzeCompletedAt}
          canAccessAiAnalysis={canAccessAiAnalysis}
          showUpgradePrompt={showGuruUpgradePrompt}
          onCloseUpgradePrompt={closeGuruUpgradePrompt}
          onOpenUpgradeModal={openFullUpgradeModal}
          isAutoSubscribing={isAutoSubscribing}
        />
      )}

      {/* Subscription Upgrade Prompt - shown when user tries to access replay without subscription */}
      <ReplayUpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={closeUpgradePrompt}
        onUpgrade={openFullUpgradeModal}
        currentPlan={subscriptionPlan as any}
        feature={upgradeFeature}
      />

      {/* Full Upgrade Modal - multi-step subscription flow */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={closeUpgradeModal}
        currentPlan={subscriptionPlan as any}
        requiredFeature={upgradeFeature}
        highlightPlan={upgradeFeature === 'tickReplay' ? 'plus' : (upgradeFeature as string) === 'aiAnalysis' || (upgradeFeature as string) === 'api' ? 'premium' : 'essential'}
      />

      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={isTermsOpen}
        onClose={closeTermsModal}
      />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={isPrivacyOpen}
        onClose={closePrivacyModal}
      />

      {/* Footer with Terms of Service link - only show on homepage */}
      {candles.length === 0 && replayMode === 'off' && (
        <footer className="fixed bottom-0 left-0 right-0 bg-slate-950/80 backdrop-blur-sm border-t border-slate-800 py-2 px-4 z-10">
          <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
            <span>© 2026 TradeRewind</span>
            <span>•</span>
            <button
              onClick={openTermsModal}
              className="hover:text-blue-400 transition-colors underline"
            >
              Terms of Service
            </button>
            <span>•</span>
            <button
              onClick={openPrivacyModal}
              className="hover:text-blue-400 transition-colors underline"
            >
              Privacy Policy
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

export default App;
